import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Modal,
  Button,
  Form,
  Row,
  Col,
  Card,
  Badge,
  Alert,
  ListGroup
} from 'react-bootstrap';
import {
  FaPlus,
  FaTrash,
  FaEdit,
  FaCog,
  FaGripVertical,
  FaCheck,
  FaTimes,
  FaInfoCircle
} from 'react-icons/fa';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import DataApi from '../../api/DataApi';
import ObjectConfigApi from '../../api/ObjectConfigApi';
import ScoringRuleApi from '../../api/ScoringRuleApi';
import SearchViewApi from '../../api/SearchViewApi';
import {
  getOperatorsForDataType,
  defaultOperatorForDataType,
  isPersistableFilterBinding,
  filterBindingRequiresSourceField,
  effectiveFilterBindingOperator
} from '../../utils/searchFilterOperators';
import './css/LayoutWidgetManager.css';

const MAX_FIELDS_FOR_CARD = 4;
/** Keep in sync with layout widget API search validation */
const MAX_SEARCH_WIDGET_DISPLAY_FIELDS = 60;

function uniqueSearchWidgetFields(fields) {
  const seen = new Set();
  return (Array.isArray(fields) ? fields : []).filter((f) => {
    if (f == null || f === '' || seen.has(f)) return false;
    seen.add(f);
    return true;
  });
}

function parseLayoutWidgetConfig(config) {
  if (!config) return {};
  if (typeof config === 'string') {
    try {
      return JSON.parse(config);
    } catch {
      return {};
    }
  }
  return config;
}

/**
 * Detail-tab search widgets (Related List Manager) must not appear in Manage Widgets columns.
 * Only sidebar search widgets (show_in_detail_tabs === false) are listed here.
 */
function isWidgetShownInManageColumns(widget) {
  if (widget.widget_type !== 'search') return true;
  const cfg = parseLayoutWidgetConfig(widget.config);
  return cfg.show_in_detail_tabs === false;
}

const LayoutWidgetManager = ({ objectName, layoutId, onClose, onSave }) => {
  const [availableRelatedLists, setAvailableRelatedLists] = useState([]);
  /** All related lists for the source object (unfiltered) — used by map "route" widgets
   *  which can re-use a relationship that's already consumed by a relatedlist widget. */
  const [allRelatedLists, setAllRelatedLists] = useState([]);
  const [layoutWidgets, setLayoutWidgets] = useState([]);
  const widgetsForManageColumns = useMemo(
    () => layoutWidgets.filter(isWidgetShownInManageColumns),
    [layoutWidgets]
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [selectedRelatedList, setSelectedRelatedList] = useState(null);
  const [editingWidgetId, setEditingWidgetId] = useState(null);
  const [picklistFields, setPicklistFields] = useState([]);
  const [layoutMetadata, setLayoutMetadata] = useState({
    total_columns: 3,
    detail_column_position: 1
  });
  const [hasActiveScoringRule, setHasActiveScoringRule] = useState(false);
  const [checkingScoringRule, setCheckingScoringRule] = useState(false);
  const [availableSearchViews, setAvailableSearchViews] = useState([]); // All configured search views
  const [sourceObjectFields, setSourceObjectFields] = useState([]);
  const [searchViewFields, setSearchViewFields] = useState([]); // Display fields from selected search view
  const [filterableFields, setFilterableFields] = useState([]); // Filterable fields from selected search view
  const [searchViewConfig, setSearchViewConfig] = useState(null); // Full search view config
  const [searchViewConfigsCache, setSearchViewConfigsCache] = useState({}); // Cache configs per search view ID: { [searchViewId]: { fields: [], filter_bindings: [] } }
  /** What we're adding/editing in the configure modal — avoids mis-detecting search from filter_bindings: [] */
  const [widgetDraftKind, setWidgetDraftKind] = useState(null); // 'search' | 'activity' | 'score' | 'relatedlist' | 'map' | null
  const [configForm, setConfigForm] = useState({
    widget_label: '',
    widget_column_position: 3,
    fields: [],
    maxRecords: 10,
    maxHeight: 400, // Default max height in pixels
    picklistField: '', // Selected picklist field for tab filtering
    search_view_id: '', // For search widgets - selected search view ID
    search_view_name: '', // URL slug for /search/{object}/{name}
    filter_bindings: [], // Search widgets: [{ filterable_field, source_field, operator? }]
    display_as_table: false,
    // Map widget config (widget_type === 'map')
    map_data_source: 'record', // 'record' | 'route'
    map_coord_source_mode: 'combined', // 'combined' | 'split' | 'address'
    map_lat_field: '',
    map_lng_field: '',
    map_latlng_field: '',
    map_address_fields: [],
    map_zoom: 14,
    // Route mode (data_source === 'route')
    map_route_related_list_id: '',
    map_route_coord_source_mode: 'combined', // 'combined' | 'split' | 'address'
    map_route_lat_field: '',
    map_route_lng_field: '',
    map_route_latlng_field: '',
    map_route_address_fields: [],
    map_route_label_field: '',
    map_route_order_field: '',
    map_route_max_stops: 25,
    map_route_connect: 'polyline' // 'none' | 'polyline' | 'directions'
  });

  // Debug: Log layoutId when component mounts or it changes
  useEffect(() => {
    console.log('🚀 [LayoutWidgetManager] Component mounted/updated');
    console.log('🚀 [LayoutWidgetManager] layoutId received:', layoutId);
    console.log('🚀 [LayoutWidgetManager] objectName:', objectName);
    if (!layoutId) {
      console.error('⚠️ [LayoutWidgetManager] layoutId is missing!');
    }
  }, [layoutId, objectName]);

  // Auto-dismiss success messages
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => {
        setSuccess('');
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  const fetchLayoutWidgets = async () => {
    try {
      if (!layoutId) {
        console.error('❌ fetchLayoutWidgets: layoutId is missing!');
        setError('Layout ID is missing. Please close and reopen the widget manager.');
        return;
      }

      console.log('📡 Fetching widgets for layout ID:', layoutId);
      const api = new DataApi(`layoutwidget/layout/${layoutId}`);
      const response = await api.fetchAll();

      console.log('📦 Full response:', response);
      console.log('📦 Response data:', response?.data);
      console.log('📦 Response data.data:', response?.data?.data);
      console.log('📦 Response data.layout_metadata:', response?.data?.layout_metadata);

      if (response?.data?.success) {
        // Handle both old format (array) and new format (object with widgets property)
        let widgetsArray = [];
        if (Array.isArray(response.data.data)) {
          widgetsArray = response.data.data;
        } else if (response.data.data?.widgets && Array.isArray(response.data.data.widgets)) {
          widgetsArray = response.data.data.widgets;
        } else {
          console.warn('⚠️ Unexpected data format:', response.data.data);
          widgetsArray = [];
        }

        const widgets = widgetsArray.map(widget => ({
          ...widget,
          config: typeof widget.config === 'string' ? JSON.parse(widget.config) : widget.config
        }));
        console.log('✅ Parsed widgets:', widgets);
        setLayoutWidgets(widgets);

        // Extract and set layout metadata from response
        const metadata = response.data.layout_metadata || response.data.data?.layout_metadata;
        if (metadata) {
          setLayoutMetadata({
            total_columns: metadata.total_columns || 3,
            detail_column_position: metadata.detail_column_position || 1
          });
          console.log('✅ Layout metadata loaded from widgets response:', metadata);
        } else {
          // Set defaults if metadata not found
          console.warn('⚠️ Layout metadata not found in response, using defaults');
          setLayoutMetadata({
            total_columns: 3,
            detail_column_position: 1
          });
        }
      } else {
        console.error('❌ Response not successful:', response?.data);
        setLayoutWidgets([]);
        setError(response?.data?.message || 'Failed to load widgets');
      }
    } catch (error) {
      console.error('❌ Error fetching layout widgets:', error);
      console.error('❌ Error details:', error.response?.data || error.message);
      setLayoutWidgets([]);
      setError('Failed to load widgets: ' + (error.message || 'Unknown error'));
    }
  };

  const fetchAvailableRelatedLists = useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      // Fetch all related lists for the object directly from related_list_config (same as RelatedListManager)
      const api = new DataApi(`relatedlist/${objectName}`);
      const response = await api.fetchAll();

      if (response?.data?.success) {
        const allRelatedListsRaw = response.data.data || [];
        console.log('Fetched all related lists from related_list_config:', allRelatedListsRaw);

        // Filter out already used related lists based on current layoutWidgets state
        // This ensures widgets that are already added don't appear in available list
        const usedComponentIds = new Set(
          (layoutWidgets || [])
            .filter((w) => w?.widget_type === 'relatedlist' && w?.component_id != null && w?.component_id !== '')
            .map((w) => String(w.component_id))
        );

        // De-duplicate related lists from API by id (sometimes backend returns duplicates)
        const uniqueById = new Map();
        for (const rl of allRelatedListsRaw) {
          const id = rl?.id != null ? String(rl.id) : null;
          if (!id) continue;
          if (!uniqueById.has(id)) uniqueById.set(id, rl);
        }
        const allRelatedListsList = Array.from(uniqueById.values());
        setAllRelatedLists(allRelatedListsList);

        const available = allRelatedListsList.filter((rl) => {
          const id = rl?.id != null ? String(rl.id) : null;
          if (!id) return false;
          return !usedComponentIds.has(id);
        });

        console.log('Available related lists (after filtering used widgets):', available);
        console.log('Used component IDs:', Array.from(usedComponentIds));
        setAvailableRelatedLists(available);
      } else {
        setAvailableRelatedLists([]);
        setAllRelatedLists([]);
      }
    } catch (error) {
      console.error('Error fetching available related lists:', error);
      setError('Failed to load available related lists: ' + (error.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  }, [objectName, layoutWidgets]);


  // Calculate available widget column positions based on layout configuration
  const getAvailableWidgetPositions = (totalColumns, detailColumnPosition) => {
    const available = [];
    for (let i = 1; i <= totalColumns; i++) {
      if (i !== detailColumnPosition) {
        available.push(i);
      }
    }
    return available;
  };

  useEffect(() => {
    console.log('🔄 useEffect - objectName:', objectName, 'layoutId:', layoutId);
    if (objectName && layoutId) {
      console.log('✅ Fetching widgets for layout:', layoutId);
      fetchLayoutWidgets(); // This now includes layout metadata
    } else {
      console.warn('⚠️ Missing required props:', { objectName, layoutId });
    }
  }, [objectName, layoutId]);

  // Refresh available lists when widgets change or objectName changes
  useEffect(() => {
    if (objectName) {
      fetchAvailableRelatedLists();
    }
  }, [layoutWidgets, objectName, fetchAvailableRelatedLists]);

  // Check for active scoring rule when objectName changes
  useEffect(() => {
    const checkActiveScoringRule = async () => {
      if (!objectName) {
        setHasActiveScoringRule(false);
        return;
      }

      setCheckingScoringRule(true);
      try {
        const scoringRuleApi = new ScoringRuleApi();
        const rulesResponse = await scoringRuleApi.fetchByObject(objectName, true);

        console.log('LayoutWidgetManager - Scoring rule check:', {
          objectName,
          response: rulesResponse?.data,
          rulesCount: rulesResponse?.data?.data?.length
        });

        if (rulesResponse?.data?.success && rulesResponse.data.data?.length > 0) {
          const rule = rulesResponse.data.data[0];
          const isActive = rule.is_active === true || rule.is_active === undefined;
          console.log('LayoutWidgetManager - Found scoring rule, isActive:', isActive);
          setHasActiveScoringRule(isActive);
        } else {
          console.log('LayoutWidgetManager - No active scoring rule found for object:', objectName);
          setHasActiveScoringRule(false);
        }
      } catch (error) {
        console.error('LayoutWidgetManager - Error fetching scoring rule:', error);
        setHasActiveScoringRule(false);
      } finally {
        setCheckingScoringRule(false);
      }
    };

    checkActiveScoringRule();
  }, [objectName]);

  const refreshSearchViews = useCallback(async () => {
    try {
      const searchViewApi = new SearchViewApi();
      const response = await searchViewApi.fetchAll();
      if (response?.data?.success && Array.isArray(response.data.data)) {
        const searchViews = response.data.data;
        const searchViewOptions = searchViews.map((sv) => ({
          id: sv.id,
          object_name: sv.object_name,
          object_label: sv.object_label || sv.object_name,
          view_label: sv.view_label,
          search_view_name: sv.search_view_name,
          display_fields: sv.display_fields || [],
          filterable_fields: sv.filterable_fields || [],
          fullData: sv
        }));
        setAvailableSearchViews(searchViewOptions);
      } else {
        setAvailableSearchViews([]);
      }
    } catch (error) {
      console.error('[LayoutWidgetManager] refreshSearchViews:', error);
      setAvailableSearchViews([]);
    }
  }, []);

  useEffect(() => {
    refreshSearchViews();
  }, [refreshSearchViews]);

  // Fetch source object fields for field bindings
  useEffect(() => {
    const fetchSourceFields = async () => {
      if (!objectName) return;
      try {
        const response = await ObjectConfigApi.fetchConfigObjectByNameWithFields(objectName);
        if (response?.data?.fields) {
          setSourceObjectFields(response.data.fields.map(f => ({
            field_name: f.field_name,
            field_label: f.field_label || f.field_name,
            data_type: f.data_type
          })));
        }
      } catch (error) {
        console.error('Error fetching source object fields:', error);
      }
    };

    fetchSourceFields();
  }, [objectName]);

  // Fetch search view configuration and fields when search view is selected
  useEffect(() => {
    const fetchSearchViewFields = async () => {
      if (!configForm.search_view_id) {
        setSearchViewFields([]);
        setFilterableFields([]);
        setSearchViewConfig(null);
        return;
      }

      try {
        console.log('📋 [LayoutWidgetManager] fetchSearchViewFields - Looking for search view ID:', configForm.search_view_id);

        // Use the data we already have from availableSearchViews instead of calling API
        const selectedSearchView = availableSearchViews.find(sv => sv.id === configForm.search_view_id);

        if (!selectedSearchView) {
          console.warn('📋 [LayoutWidgetManager] fetchSearchViewFields - Search view not found in availableSearchViews');
          setSearchViewFields([]);
          setFilterableFields([]);
          setSearchViewConfig(null);
          return;
        }

        // Use the full data if available, otherwise use the stored data
        const config = selectedSearchView.fullData || selectedSearchView;
        console.log('📋 [LayoutWidgetManager] fetchSearchViewFields - Using cached search view config:', config);
        console.log('📋 [LayoutWidgetManager] fetchSearchViewFields - config.display_fields:', config.display_fields);
        console.log('📋 [LayoutWidgetManager] fetchSearchViewFields - config.display_fields type:', typeof config.display_fields);
        console.log('📋 [LayoutWidgetManager] fetchSearchViewFields - config.display_fields isArray:', Array.isArray(config.display_fields));
        console.log('📋 [LayoutWidgetManager] fetchSearchViewFields - config.filterable_fields:', config.filterable_fields);
        console.log('📋 [LayoutWidgetManager] fetchSearchViewFields - config.filterable_fields type:', typeof config.filterable_fields);
        console.log('📋 [LayoutWidgetManager] fetchSearchViewFields - config.filterable_fields isArray:', Array.isArray(config.filterable_fields));

        setSearchViewConfig(config);

          // Fetch object config to get field labels
          const searchViewObjectName = config.object_name;
          console.log('📋 [LayoutWidgetManager] fetchSearchViewFields - Fetching object config for:', searchViewObjectName);

          let allFields = [];
          try {
            const objectConfigResponse = await ObjectConfigApi.fetchConfigObjectByNameWithFields(searchViewObjectName);
            allFields = objectConfigResponse?.data?.fields || objectConfigResponse?.fields || [];
            console.log('📋 [LayoutWidgetManager] fetchSearchViewFields - All fields from object config:', allFields);
            console.log('📋 [LayoutWidgetManager] fetchSearchViewFields - All fields count:', allFields.length);
          } catch (objError) {
            console.error('📋 [LayoutWidgetManager] fetchSearchViewFields - Error fetching object config:', objError);
            // Continue even if object config fetch fails - we'll use field names as labels
          }

          // Map display_fields to field objects with labels
          let displayFieldsArray = [];
          if (Array.isArray(config.display_fields)) {
            displayFieldsArray = config.display_fields;
          } else if (typeof config.display_fields === 'string') {
            try {
              // Try parsing as JSON string
              displayFieldsArray = JSON.parse(config.display_fields);
            } catch (e) {
              // If not JSON, try splitting by comma
              console.log('📋 [LayoutWidgetManager] fetchSearchViewFields - display_fields is string, trying to parse:', config.display_fields);
              displayFieldsArray = config.display_fields.split(',').map(f => f.trim()).filter(f => f);
            }
          } else if (config.display_fields) {
            // Handle other types
            console.warn('📋 [LayoutWidgetManager] fetchSearchViewFields - Unexpected display_fields type:', typeof config.display_fields);
            displayFieldsArray = [];
          }

          console.log('📋 [LayoutWidgetManager] fetchSearchViewFields - Parsed displayFieldsArray:', displayFieldsArray);

          const displayFields = displayFieldsArray.map(fieldName => {
            const fieldInfo = allFields.find(f => f.field_name === fieldName);
            return {
              field_name: fieldName,
              field_label: fieldInfo?.field_label || fieldName,
              data_type: fieldInfo?.data_type || 'text'
            };
          });
          console.log('📋 [LayoutWidgetManager] fetchSearchViewFields - Mapped displayFields:', displayFields);
          setSearchViewFields(displayFields);

          // Map filterable_fields to field objects with labels
          let filterableFieldsArray = [];
          if (Array.isArray(config.filterable_fields)) {
            filterableFieldsArray = config.filterable_fields;
          } else if (typeof config.filterable_fields === 'string') {
            try {
              // Try parsing as JSON string
              filterableFieldsArray = JSON.parse(config.filterable_fields);
            } catch (e) {
              // If not JSON, try splitting by comma
              console.log('📋 [LayoutWidgetManager] fetchSearchViewFields - filterable_fields is string, trying to parse:', config.filterable_fields);
              filterableFieldsArray = config.filterable_fields.split(',').map(f => f.trim()).filter(f => f);
            }
          } else if (config.filterable_fields) {
            // Handle other types
            console.warn('📋 [LayoutWidgetManager] fetchSearchViewFields - Unexpected filterable_fields type:', typeof config.filterable_fields);
            filterableFieldsArray = [];
          }

          console.log('📋 [LayoutWidgetManager] fetchSearchViewFields - Parsed filterableFieldsArray:', filterableFieldsArray);

          const filterFields = filterableFieldsArray.map(fieldName => {
            const fieldInfo = allFields.find(f => f.field_name === fieldName);
            return {
              field_name: fieldName,
              field_label: fieldInfo?.field_label || fieldName,
              data_type: fieldInfo?.data_type || 'text'
            };
          });
          console.log('📋 [LayoutWidgetManager] fetchSearchViewFields - Mapped filterFields:', filterFields);
          setFilterableFields(filterFields);

          console.log('📋 [LayoutWidgetManager] fetchSearchViewFields - Display fields:', displayFields);
          console.log('📋 [LayoutWidgetManager] fetchSearchViewFields - Filterable fields:', filterFields);

          // Check if we have cached configuration for this search view
          const cachedConfig = searchViewConfigsCache[configForm.search_view_id];

          // Initialize filter_bindings for each filterable field (only when adding new widget, not editing)
          setConfigForm(prev => {
            const updates = { ...prev };

            // If we have cached config for this search view, restore it
            if (cachedConfig) {
              console.log('📋 [LayoutWidgetManager] fetchSearchViewFields - Restoring cached config for search view:', configForm.search_view_id);
              updates.fields = cachedConfig.fields || [];
              updates.filter_bindings = cachedConfig.filter_bindings || [];
            } else {
              // No cached config - initialize defaults
              // Pre-select first 4 display fields if none are selected
              if (prev.fields.length === 0 && displayFields.length > 0 && !editingWidgetId) {
                const fieldsToSelect = displayFields.slice(0, MAX_FIELDS_FOR_CARD).map(f => f.field_name);
                console.log('📋 [LayoutWidgetManager] fetchSearchViewFields - Pre-selecting display fields:', fieldsToSelect);
                updates.fields = fieldsToSelect;
              }

              // Initialize filter_bindings for each filterable field if not editing
              if (!editingWidgetId && filterFields.length > 0) {
                const existingBindings = prev.filter_bindings || [];
                const newBindings = filterFields.map(filterField => {
                  const existing = existingBindings.find(b => b.filterable_field === filterField.field_name);
                  if (existing) {
                    return {
                      ...existing,
                      operator: existing.operator || defaultOperatorForDataType(filterField.data_type)
                    };
                  }
                  return {
                    filterable_field: filterField.field_name,
                    source_field: '',
                    operator: defaultOperatorForDataType(filterField.data_type)
                  };
                });
                console.log('📋 [LayoutWidgetManager] fetchSearchViewFields - Initializing filter_bindings:', newBindings);
                updates.filter_bindings = newBindings;
              }
            }

            return updates;
          });
      } catch (error) {
        console.error('📋 [LayoutWidgetManager] fetchSearchViewFields - Error:', error);
        setSearchViewFields([]);
        setFilterableFields([]);
        setSearchViewConfig(null);
      }
    };

    fetchSearchViewFields();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configForm.search_view_id, editingWidgetId, availableSearchViews, searchViewConfigsCache]);

  const handleAddActivityWidget = () => {
    setSelectedRelatedList(null);
    setEditingWidgetId(null);
    setWidgetDraftKind('activity');
    console.log('LayoutWidgetManager - layoutMetadata:', layoutMetadata);
    const availablePositions = getAvailableWidgetPositions(layoutMetadata.total_columns, layoutMetadata.detail_column_position);
    const defaultPosition = availablePositions.length > 0 ? availablePositions[0] : 3;
    setConfigForm({
      widget_label: 'Activity Timeline',
      widget_column_position: defaultPosition,
      fields: [],
      maxRecords: 10,
      maxHeight: 600,
      picklistField: ''
    });
    setShowConfigModal(true);
  };

  const handleAddScoreWidget = () => {
    setSelectedRelatedList(null);
    setEditingWidgetId(null);
    setWidgetDraftKind('score');
    const availablePositions = getAvailableWidgetPositions(layoutMetadata.total_columns, layoutMetadata.detail_column_position);
    const defaultPosition = availablePositions.length > 0 ? availablePositions[0] : 3;
    setConfigForm({
      widget_label: 'Lead Score',
      widget_column_position: defaultPosition,
      fields: [],
      maxRecords: 10,
      maxHeight: 400,
      picklistField: '',
      search_view_object_name: '',
      field_bindings: []
    });
    setShowConfigModal(true);
  };

  const handleAddSearchWidget = () => {
    setSelectedRelatedList(null);
    setEditingWidgetId(null);
    setWidgetDraftKind('search');
    setSearchViewConfigsCache({});
    const positions = getAvailableWidgetPositions(layoutMetadata.total_columns, layoutMetadata.detail_column_position);
    const defaultPosition = positions.length > 0 ? positions[0] : 3;
    setConfigForm({
      widget_label: 'Search Results',
      widget_column_position: defaultPosition,
      fields: [],
      maxRecords: 10,
      maxHeight: 400,
      picklistField: '',
      search_view_id: '',
      search_view_name: '',
      filter_bindings: [],
      display_as_table: false
    });
    setShowConfigModal(true);
  };

  const handleAddMapWidget = () => {
    setSelectedRelatedList(null);
    setEditingWidgetId(null);
    setWidgetDraftKind('map');
    const availablePositions = getAvailableWidgetPositions(layoutMetadata.total_columns, layoutMetadata.detail_column_position);
    const defaultPosition = availablePositions.length > 0 ? availablePositions[0] : 3;
    setConfigForm({
      widget_label: 'Map',
      widget_column_position: defaultPosition,
      fields: [],
      maxRecords: 10,
      maxHeight: 400,
      picklistField: '',
      search_view_id: '',
      search_view_name: '',
      filter_bindings: [],
      display_as_table: false,
      map_data_source: 'record',
      map_coord_source_mode: 'combined',
      map_lat_field: '',
      map_lng_field: '',
      map_latlng_field: '',
      map_address_fields: [],
      map_zoom: 14,
      map_route_related_list_id: '',
      map_route_coord_source_mode: 'combined',
      map_route_lat_field: '',
      map_route_lng_field: '',
      map_route_latlng_field: '',
      map_route_address_fields: [],
      map_route_label_field: '',
      map_route_order_field: '',
      map_route_max_stops: 25,
      map_route_connect: 'polyline'
    });
    setShowConfigModal(true);
  };

  const addSearchWidgetDisplayField = (fieldName) => {
    if (!fieldName) return;
    setConfigForm((prev) => {
      const selected = prev.fields || [];
      const cap = Math.min(
        searchViewFields.length,
        MAX_SEARCH_WIDGET_DISPLAY_FIELDS
      );
      if (selected.length >= cap || selected.includes(fieldName)) {
        return prev;
      }
      return { ...prev, fields: [...selected, fieldName] };
    });
    setError('');
  };

  const removeSearchWidgetDisplayField = (fieldName) => {
    setConfigForm((prev) => ({
      ...prev,
      fields: (prev.fields || []).filter((f) => f !== fieldName)
    }));
    setError('');
  };

  const handleSearchWidgetSelectedFieldsDragEnd = (result) => {
    if (!result.destination) return;
    if (
      result.source.droppableId !== 'lw-search-selected-fields' ||
      result.destination.droppableId !== 'lw-search-selected-fields'
    ) {
      return;
    }
    setConfigForm((prev) => {
      const selected = [...(prev.fields || [])];
      const [removed] = selected.splice(result.source.index, 1);
      selected.splice(result.destination.index, 0, removed);
      return { ...prev, fields: selected };
    });
  };

  const handleAddRelatedList = async (relatedList) => {
    setWidgetDraftKind('relatedlist');
    // Check if this is Activity Timeline
    const isActivityTimeline = relatedList.target_object_name === 'activity' &&
                               relatedList.source_field_name === 'id' &&
                               relatedList.target_field_name === 'related_record_id';

    // Get available fields from related list
    const availableFields = relatedList.object_fields || [];

    // Pre-select first 4 fields (or all if less than 4) - but for Activity Timeline, we'll use fixed fields
    const selectedFields = isActivityTimeline
      ? ['id', 'name', 'activity_type', 'parent_object'] // Fixed fields for Activity Timeline
      : availableFields
          .slice(0, MAX_FIELDS_FOR_CARD)
          .map(field => field.field_name)
          .filter(Boolean);

    setSelectedRelatedList(relatedList);
    console.log('LayoutWidgetManager - layoutMetadata:', layoutMetadata);
    const availablePositions = getAvailableWidgetPositions(layoutMetadata.total_columns, layoutMetadata.detail_column_position);
    console.log('LayoutWidgetManager - availablePositions:', availablePositions);
    const defaultPosition = availablePositions.length > 0 ? availablePositions[0] : 3;
    setConfigForm({
      widget_label: relatedList.relationship_label || relatedList.target_object_name,
      widget_column_position: defaultPosition,
      fields: selectedFields,
      maxRecords: isActivityTimeline ? 100 : 10,
      maxHeight: isActivityTimeline ? 600 : 400,
      picklistField: '' // Always empty for Activity Timeline
    });

    // Only fetch picklist fields for non-Activity Timeline related lists
    if (!isActivityTimeline) {
      await fetchPicklistFields(relatedList.target_object_name);
    }

    setShowConfigModal(true);
  };

  const fetchPicklistFields = async (targetObjectName) => {
    if (!targetObjectName) {
      setPicklistFields([]);
      return;
    }

    try {
      const response = await ObjectConfigApi.fetchConfigObjectByNameWithFields(targetObjectName);
      const fields = response?.data?.fields || response?.fields || [];

      // Filter for picklist fields only
      const picklistFieldsList = fields.filter(field =>
        field.data_type === 'picklist' || field.data_type === 'multipicklist'
      );

      setPicklistFields(picklistFieldsList);
    } catch (error) {
      console.error('Error fetching picklist fields:', error);
      setPicklistFields([]);
    }
  };

  const handleSaveWidget = async () => {
    try {
      setLoading(true);
      setError('');

      const isActivityWidget = widgetDraftKind === 'activity';
      const isScoreWidget = widgetDraftKind === 'score';
      const isSearchWidget = widgetDraftKind === 'search';
      const isMapWidget = widgetDraftKind === 'map';

      // Helper to build & validate map widget config (record or route mode)
      const buildMapWidgetConfig = () => {
        const dataSource = configForm.map_data_source || 'record';
        const baseCfg = {
          data_source: dataSource,
          zoom: parseInt(configForm.map_zoom, 10) || 14,
          max_height: configForm.maxHeight || 400
        };

        if (dataSource === 'record') {
          const mode = configForm.map_coord_source_mode || 'combined';
          const fields = (configForm.map_address_fields || []).filter(Boolean);
          baseCfg.coord_source_mode = mode;
          if (mode === 'combined') {
            if (fields.length !== 1) return { error: 'Combined mode needs exactly 1 field' };
            baseCfg.latlng_field = fields[0];
          } else if (mode === 'split') {
            if (fields.length !== 2) return { error: 'Separate Lat/Lng mode needs exactly 2 fields (lat first, lng second)' };
            baseCfg.lat_field = fields[0];
            baseCfg.lng_field = fields[1];
          } else if (mode === 'address') {
            if (fields.length === 0) return { error: 'Address mode needs at least 1 field' };
            baseCfg.address_fields = fields;
          } else {
            return { error: 'Invalid coordinate source mode' };
          }
          return { config: baseCfg };
        }

        if (dataSource === 'route') {
          if (!configForm.map_route_related_list_id) {
            return { error: 'Please select a related list for the route' };
          }
          const mode = configForm.map_route_coord_source_mode || 'combined';
          const fields = (configForm.map_route_address_fields || []).filter(Boolean);
          const route = {
            related_list_id: configForm.map_route_related_list_id,
            coord_source_mode: mode,
            max_stops: parseInt(configForm.map_route_max_stops, 10) || 25,
            connect: configForm.map_route_connect || 'polyline'
          };
          if (mode === 'combined') {
            if (fields.length !== 1) return { error: 'Combined mode needs exactly 1 child field' };
            route.latlng_field = fields[0];
          } else if (mode === 'split') {
            if (fields.length !== 2) return { error: 'Separate Lat/Lng mode needs exactly 2 child fields (lat first, lng second)' };
            route.lat_field = fields[0];
            route.lng_field = fields[1];
          } else if (mode === 'address') {
            if (fields.length === 0) return { error: 'Address mode needs at least 1 child field' };
            route.address_fields = fields;
          } else {
            return { error: 'Invalid coordinate source mode' };
          }
          if (configForm.map_route_label_field) route.label_field = configForm.map_route_label_field;
          if (configForm.map_route_order_field) route.order_field = configForm.map_route_order_field;

          baseCfg.route = route;
          return { config: baseCfg };
        }

        return { error: 'Invalid data source' };
      };

      if (isActivityWidget) {
        // Activity widget - no validation needed for fields
        if (!layoutId) {
          console.error('❌ Layout ID is missing!', { layoutId, objectName });
          setError('Layout ID is missing. Please close and reopen the widget manager.');
          setLoading(false);
          return;
        }

        const widgetData = {
          layout_id: layoutId,
          widget_type: 'activity',
          widget_label: configForm.widget_label || 'Activity Timeline',
          widget_column_position: configForm.widget_column_position,
          component_id: null, // Activity widget doesn't need component_id
          config: {
            maxHeight: configForm.maxHeight || 600
          },
          display_order: layoutWidgets.length,
          is_active: true
        };

        console.log('✅ Creating activity widget with data:', widgetData);
        const api = new DataApi('layoutwidget');
        const response = await api.create(widgetData);

        if (response?.data?.success) {
          setSuccess('Activity Timeline widget added successfully');
          setShowConfigModal(false);
          setWidgetDraftKind(null);
          setSelectedRelatedList(null);
          await fetchLayoutWidgets();
          fetchAvailableRelatedLists();
          if (onSave) onSave();
        } else {
          setError('Failed to add widget: ' + (response?.data?.message || 'Unknown error'));
        }
        setLoading(false);
        return;
      }

      if (isScoreWidget) {
        // Score widget - no validation needed for fields
        if (!layoutId) {
          console.error('❌ Layout ID is missing!', { layoutId, objectName });
          setError('Layout ID is missing. Please close and reopen the widget manager.');
          setLoading(false);
          return;
        }

        const widgetData = {
          layout_id: layoutId,
          widget_type: 'score',
          widget_label: configForm.widget_label || 'Lead Score',
          widget_column_position: configForm.widget_column_position,
          component_id: null, // Score widget doesn't need component_id
          config: {
            max_height: configForm.maxHeight || 400
          },
          display_order: layoutWidgets.length,
          is_active: true
        };

        console.log('✅ Creating score widget with data:', widgetData);

        const api = new DataApi('layoutwidget');
        const response = await api.create(widgetData);

        if (response?.data?.success) {
          setSuccess('Lead Score widget added successfully');
          setShowConfigModal(false);
          setWidgetDraftKind(null);
          setSelectedRelatedList(null);
          await fetchLayoutWidgets();
          if (onSave) onSave();
        } else {
          setError('Failed to add widget: ' + (response?.data?.message || 'Unknown error'));
        }
        setLoading(false);
        return;
      }

      if (isSearchWidget) {
        // Search widget - validate search view and filter bindings
        if (!layoutId) {
          console.error('❌ Layout ID is missing!', { layoutId, objectName });
          setError('Layout ID is missing. Please close and reopen the widget manager.');
          setLoading(false);
          return;
        }

        if (!configForm.search_view_id) {
          setError('Please select a search view');
          setLoading(false);
          return;
        }

        const selectedDisplayFields = uniqueSearchWidgetFields(
          (configForm.fields || []).filter(Boolean)
        );
        if (selectedDisplayFields.length === 0) {
          setError('Please select at least one field to display in the search widget');
          setLoading(false);
          return;
        }
        if (selectedDisplayFields.length > MAX_SEARCH_WIDGET_DISPLAY_FIELDS) {
          setError(
            `Maximum ${MAX_SEARCH_WIDGET_DISPLAY_FIELDS} fields allowed for search widgets`
          );
          setLoading(false);
          return;
        }

        // Filter bindings are optional - user can bind filterable fields to current record fields
        const filterBindings = (configForm.filter_bindings || []).filter(isPersistableFilterBinding);

        const widgetData = {
          layout_id: layoutId,
          widget_type: 'search',
          widget_label: configForm.widget_label || 'Search Results',
          widget_column_position: configForm.widget_column_position,
          component_id: null, // Search widget doesn't need component_id
          config: {
            search_view_id: configForm.search_view_id,
            search_view_name: configForm.search_view_name || undefined,
            filter_bindings: filterBindings,
            fields: selectedDisplayFields, // Selected display fields
            max_records: configForm.maxRecords || 10,
            max_height: configForm.maxHeight || 400,
            display_as_table: configForm.display_as_table === true,
            show_in_detail_tabs: false
          },
          display_order: layoutWidgets.length,
          is_active: true
        };

        console.log('✅ Creating search widget with data:', widgetData);

        const api = new DataApi('layoutwidget');
        const response = await api.create(widgetData);

        if (response?.data?.success) {
          setSuccess('Search widget added successfully');
          setShowConfigModal(false);
          setWidgetDraftKind(null);
          setSelectedRelatedList(null);
          await fetchLayoutWidgets();
          if (onSave) onSave();
        } else {
          setError('Failed to add widget: ' + (response?.data?.message || 'Unknown error'));
        }
        setLoading(false);
        return;
      }

      if (isMapWidget) {
        if (!layoutId) {
          setError('Layout ID is missing. Please close and reopen the widget manager.');
          setLoading(false);
          return;
        }

        const built = buildMapWidgetConfig();
        if (built.error) {
          setError(built.error);
          setLoading(false);
          return;
        }

        const widgetData = {
          layout_id: layoutId,
          widget_type: 'map',
          widget_label: configForm.widget_label || 'Map',
          widget_column_position: configForm.widget_column_position,
          component_id: null,
          config: built.config,
          display_order: layoutWidgets.length,
          is_active: true
        };

        console.log('✅ Creating map widget with data:', widgetData);
        const api = new DataApi('layoutwidget');
        const response = await api.create(widgetData);

        if (response?.data?.success) {
          setSuccess('Map widget added successfully');
          setShowConfigModal(false);
          setWidgetDraftKind(null);
          setSelectedRelatedList(null);
          await fetchLayoutWidgets();
          if (onSave) onSave();
        } else {
          setError('Failed to add widget: ' + (response?.data?.message || 'Unknown error'));
        }
        setLoading(false);
        return;
      }

      // Check if this is Activity Timeline
      const isActivityTimeline = selectedRelatedList &&
                                 selectedRelatedList.target_object_name === 'activity' &&
                                 selectedRelatedList.source_field_name === 'id' &&
                                 selectedRelatedList.target_field_name === 'related_record_id';

      // Related list widget - existing validation (skip for Activity Timeline, Search and Map widgets)
      if (!isActivityTimeline && widgetDraftKind !== 'search' && widgetDraftKind !== 'map') {
        // Validate: Maximum 4 fields
        if (configForm.fields.length > MAX_FIELDS_FOR_CARD) {
          setError(`Maximum ${MAX_FIELDS_FOR_CARD} fields allowed for card style display`);
          return;
        }

        if (configForm.fields.length === 0) {
          setError('Please select at least one field');
          return;
        }
      }

      if (!layoutId) {
        console.error('❌ Layout ID is missing!', { layoutId, objectName });
        setError('Layout ID is missing. Please close and reopen the widget manager.');
        setLoading(false);
        return;
      }

      if (!selectedRelatedList || !selectedRelatedList.id) {
        setError('Related list is not selected');
        setLoading(false);
        return;
      }

      // Use minimal fields for Activity Timeline (just 'id' for validation - Activity Timeline fetches its own fields)
      // For other widgets, use the selected fields
      const fieldsToUse = isActivityTimeline
        ? ['id'] // Minimal field set for Activity Timeline - it doesn't use widget config fields
        : configForm.fields;

      const widgetData = {
        layout_id: layoutId,
        widget_type: 'relatedlist',
        widget_label: configForm.widget_label || selectedRelatedList.relationship_label,
        widget_column_position: configForm.widget_column_position,
        component_id: selectedRelatedList.id,
        config: {
          fields: fieldsToUse,
          maxRecords: configForm.maxRecords,
          maxHeight: configForm.maxHeight,
          picklistField: isActivityTimeline ? null : (configForm.picklistField || null)
        },
        display_order: layoutWidgets.length,
        is_active: true
      };

      console.log('✅ Creating widget with data:', widgetData);
      console.log('✅ Layout ID being sent:', layoutId);

      const api = new DataApi('layoutwidget');
      const response = await api.create(widgetData);

      if (response?.data?.success) {
        setSuccess('Widget added successfully');
        setShowConfigModal(false);
        setWidgetDraftKind(null);
        setEditingWidgetId(null);
        setSelectedRelatedList(null);
        await fetchLayoutWidgets(); // Wait for widgets to load
        // fetchAvailableRelatedLists will be called after state updates
        if (onSave) onSave();
      } else {
        setError('Failed to add widget: ' + (response?.data?.message || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error adding widget:', error);
      setError('Failed to add widget: ' + (error.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteWidget = async (widgetId) => {
    if (!window.confirm('Are you sure you want to delete this widget?')) {
      return;
    }

    try {
      setLoading(true);
      setError('');

      const api = new DataApi('layoutwidget');
      const response = await api.delete(`/${widgetId}`);

      if (response?.data?.success) {
        setSuccess('Widget deleted successfully');
        await fetchLayoutWidgets(); // Wait for widgets to load
        fetchAvailableRelatedLists(); // Then refresh available lists
        if (onSave) onSave();
      } else {
        setError('Failed to delete widget');
      }
    } catch (error) {
      console.error('Error deleting widget:', error);
      setError('Failed to delete widget: ' + (error.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  const handleEditWidget = async (widget) => {
    try {
      setLoading(true);

      // Helper function to validate and get widget column position
      const getValidWidgetPosition = (currentPosition) => {
        const availablePositions = getAvailableWidgetPositions(layoutMetadata.total_columns, layoutMetadata.detail_column_position);
        let validPosition = currentPosition;

        // If current position is not available, use first available position
        if (!availablePositions.includes(validPosition) && availablePositions.length > 0) {
          validPosition = availablePositions[0];
          console.warn(`Widget position ${currentPosition} is not available. Using ${validPosition} instead.`);
        }
        return validPosition;
      };

      // Check if this is an activity widget
      if (widget.widget_type === 'activity') {
        setSelectedRelatedList(null);
        setEditingWidgetId(widget.id);
        setWidgetDraftKind('activity');
        setConfigForm({
          widget_label: widget.widget_label,
          widget_column_position: getValidWidgetPosition(widget.widget_column_position),
          fields: [],
          maxRecords: 10,
          maxHeight: widget.config?.maxHeight || 600,
          picklistField: ''
        });
        setShowConfigModal(true);
        setLoading(false);
        return;
      }

      // Check if this is a score widget
      if (widget.widget_type === 'score') {
        setSelectedRelatedList(null);
        setEditingWidgetId(widget.id);
        setWidgetDraftKind('score');
        setConfigForm({
          widget_label: widget.widget_label,
          widget_column_position: getValidWidgetPosition(widget.widget_column_position),
          fields: [],
          maxRecords: 10,
          maxHeight: widget.config?.max_height ?? widget.config?.maxHeight ?? 400,
          picklistField: '',
          search_view_object_name: '',
          field_bindings: []
        });
        setShowConfigModal(true);
        setLoading(false);
        return;
      }

      // Check if this is a map widget
      if (widget.widget_type === 'map') {
        setSelectedRelatedList(null);
        setEditingWidgetId(widget.id);
        setWidgetDraftKind('map');
        const cfg = widget.config || {};
        const route = cfg.route || {};
        setConfigForm({
          widget_label: widget.widget_label,
          widget_column_position: getValidWidgetPosition(widget.widget_column_position),
          fields: [],
          maxRecords: 10,
          maxHeight: cfg.max_height ?? cfg.maxHeight ?? 400,
          picklistField: '',
          search_view_id: '',
          search_view_name: '',
          filter_bindings: [],
          display_as_table: false,
          map_data_source: cfg.data_source || 'record',
          map_coord_source_mode: cfg.coord_source_mode || 'split',
          map_lat_field: cfg.lat_field || '',
          map_lng_field: cfg.lng_field || '',
          map_latlng_field: cfg.latlng_field || '',
          // Project the stored record-mode coord_source_mode back into a single
          // ordered field list so the unified shuttle can render it.
          map_address_fields: (() => {
            const m = (cfg.coord_source_mode || 'split').toLowerCase();
            if (m === 'split') return [cfg.lat_field, cfg.lng_field].filter(Boolean);
            if (m === 'combined') return cfg.latlng_field ? [cfg.latlng_field] : [];
            return Array.isArray(cfg.address_fields) ? cfg.address_fields : [];
          })(),
          map_zoom: cfg.zoom || 14,
          map_route_related_list_id: route.related_list_id || '',
          map_route_coord_source_mode: route.coord_source_mode || 'split',
          map_route_lat_field: route.lat_field || '',
          map_route_lng_field: route.lng_field || '',
          map_route_latlng_field: route.latlng_field || '',
          // Project the stored mode back into a single ordered field list so
          // the unified shuttle can render it.
          map_route_address_fields: (() => {
            const m = (route.coord_source_mode || 'split').toLowerCase();
            if (m === 'split') {
              return [route.lat_field, route.lng_field].filter(Boolean);
            }
            if (m === 'combined') {
              return route.latlng_field ? [route.latlng_field] : [];
            }
            return Array.isArray(route.address_fields) ? route.address_fields : [];
          })(),
          map_route_label_field: route.label_field || '',
          map_route_order_field: route.order_field || '',
          map_route_max_stops: route.max_stops || 25,
          map_route_connect: route.connect || 'polyline'
        });
        setShowConfigModal(true);
        setLoading(false);
        return;
      }

      // Check if this is a search widget
      if (widget.widget_type === 'search') {
        setSelectedRelatedList(null);
        setEditingWidgetId(widget.id);
        setWidgetDraftKind('search');
        const widgetSearchViewId = widget.config?.search_view_id || '';
        const widgetFields = uniqueSearchWidgetFields(widget.config?.fields || []);
        const widgetFilterBindings = widget.config?.filter_bindings || [];

        // Cache the widget's configuration for its search view
        if (widgetSearchViewId) {
          setSearchViewConfigsCache(prev => ({
            ...prev,
            [widgetSearchViewId]: {
              fields: widgetFields,
              filter_bindings: widgetFilterBindings
            }
          }));
          console.log('📋 [LayoutWidgetManager] Cached widget config for search view:', widgetSearchViewId, {
            fields: widgetFields,
            filter_bindings: widgetFilterBindings
          });
        }

        setConfigForm({
          widget_label: widget.widget_label,
          widget_column_position: getValidWidgetPosition(widget.widget_column_position),
          fields: widgetFields,
          maxRecords: widget.config?.max_records || 10,
          maxHeight: widget.config?.max_height || 400,
          picklistField: '',
          search_view_id: widgetSearchViewId,
          search_view_name: widget.config?.search_view_name || '',
          filter_bindings: widgetFilterBindings,
          display_as_table: widget.config?.display_as_table === true
        });
        setShowConfigModal(true);
        setLoading(false);
        return;
      }

      // Related list widget - fetch the related list details to get fields
      const api = new DataApi(`relatedlist/id/${widget.component_id}`);
      const response = await api.fetchAll();

      let relatedList;
      if (response?.data?.success) {
        relatedList = response.data.data;
        setSelectedRelatedList(relatedList);
      } else {
        // Fallback if fetch fails
        relatedList = {
          id: widget.component_id,
          relationship_label: widget.related_list_label || 'Related List',
          object_fields: [],
          target_object_name: widget.target_object_name
        };
        setSelectedRelatedList(relatedList);
      }

      // Check if this is Activity Timeline
      const isActivityTimeline = relatedList.target_object_name === 'activity' &&
                                 relatedList.source_field_name === 'id' &&
                                 relatedList.target_field_name === 'related_record_id';

      // Only fetch picklist fields for non-Activity Timeline related lists
      const targetObjectName = relatedList.target_object_name || widget.target_object_name;
      if (targetObjectName && !isActivityTimeline) {
        await fetchPicklistFields(targetObjectName);
      }

      setEditingWidgetId(widget.id);
      setWidgetDraftKind('relatedlist');
      setConfigForm({
        widget_label: widget.widget_label,
        widget_column_position: getValidWidgetPosition(widget.widget_column_position),
        fields: widget.config?.fields || [],
        maxRecords: widget.config?.maxRecords || (isActivityTimeline ? 100 : 10),
        maxHeight: widget.config?.maxHeight || (isActivityTimeline ? 600 : 400),
        picklistField: isActivityTimeline ? '' : (widget.config?.picklistField || '')
      });
      setShowConfigModal(true);
    } catch (error) {
      console.error('Error fetching related list for edit:', error);
      setError('Failed to load related list details');
      // Still open modal with basic info
      const relatedList = {
        id: widget.component_id,
        relationship_label: widget.related_list_label || 'Related List',
        object_fields: [],
        target_object_name: widget.target_object_name,
        source_field_name: 'id',
        target_field_name: widget.target_object_name === 'activity' ? 'related_record_id' : null
      };
      setSelectedRelatedList(relatedList);

      // Check if this is Activity Timeline
      const isActivityTimeline = widget.target_object_name === 'activity';

      // Only fetch picklist fields for non-Activity Timeline related lists
      if (widget.target_object_name && !isActivityTimeline) {
        await fetchPicklistFields(widget.target_object_name);
      }

      setEditingWidgetId(widget.id);
      setWidgetDraftKind('relatedlist');
      setConfigForm({
        widget_label: widget.widget_label,
        widget_column_position: widget.widget_column_position,
        fields: widget.config?.fields || [],
        maxRecords: widget.config?.maxRecords || (isActivityTimeline ? 100 : 10),
        maxHeight: widget.config?.maxHeight || (isActivityTimeline ? 600 : 400),
        picklistField: isActivityTimeline ? '' : (widget.config?.picklistField || '')
      });
      setShowConfigModal(true);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateWidget = async () => {
    try {
      setLoading(true);
      setError('');

      // Check if this is an activity widget
      const isActivityWidget = !selectedRelatedList && (editingWidgetId && layoutWidgets.find(w => w.id === editingWidgetId)?.widget_type === 'activity');
      const isScoreWidget = !selectedRelatedList && (editingWidgetId && layoutWidgets.find(w => w.id === editingWidgetId)?.widget_type === 'score');
      const isSearchWidget = !selectedRelatedList && (editingWidgetId && layoutWidgets.find(w => w.id === editingWidgetId)?.widget_type === 'search');
      const isMapWidget = !selectedRelatedList && (editingWidgetId && layoutWidgets.find(w => w.id === editingWidgetId)?.widget_type === 'map');

      if (isActivityWidget) {
        // Activity widget - no validation needed
        if (!editingWidgetId) {
          setError('Widget ID not found');
          return;
        }

        const updateData = {
          widget_label: configForm.widget_label,
          widget_column_position: configForm.widget_column_position,
          config: {
            maxHeight: configForm.maxHeight || 600
          }
        };

        const api = new DataApi('layoutwidget');
        const response = await api.put(`/${editingWidgetId}`, updateData);

        if (response?.data?.success) {
          setSuccess('Activity Timeline widget updated successfully');
          setShowConfigModal(false);
          setWidgetDraftKind(null);
          setEditingWidgetId(null);
          setSelectedRelatedList(null);
          await fetchLayoutWidgets();
          fetchAvailableRelatedLists();
          if (onSave) onSave();
        } else {
          setError('Failed to update widget: ' + (response?.data?.message || 'Unknown error'));
        }
        setLoading(false);
        return;
      }

      if (isScoreWidget) {
        // Score widget - no validation needed
        if (!editingWidgetId) {
          setError('Widget ID not found');
          return;
        }

        const updateData = {
          widget_label: configForm.widget_label,
          widget_column_position: configForm.widget_column_position,
          config: {
            max_height: configForm.maxHeight || 400
          }
        };

        const api = new DataApi('layoutwidget');
        const response = await api.put(`/${editingWidgetId}`, updateData);

        if (response?.data?.success) {
          setSuccess('Lead Score widget updated successfully');
          setShowConfigModal(false);
          setWidgetDraftKind(null);
          setEditingWidgetId(null);
          setSelectedRelatedList(null);
          await fetchLayoutWidgets();
          if (onSave) onSave();
        } else {
          setError('Failed to update widget: ' + (response?.data?.message || 'Unknown error'));
        }
        setLoading(false);
        return;
      }

      if (isSearchWidget) {
        if (!editingWidgetId) {
          setError('Widget ID not found');
          setLoading(false);
          return;
        }

        if (!configForm.search_view_id) {
          setError('Please select a search view');
          setLoading(false);
          return;
        }

        const selectedDisplayFields = uniqueSearchWidgetFields(
          (configForm.fields || []).filter(Boolean)
        );
        if (selectedDisplayFields.length === 0) {
          setError('Please select at least one field to display in the search widget');
          setLoading(false);
          return;
        }
        if (selectedDisplayFields.length > MAX_SEARCH_WIDGET_DISPLAY_FIELDS) {
          setError(
            `Maximum ${MAX_SEARCH_WIDGET_DISPLAY_FIELDS} fields allowed for search widgets`
          );
          setLoading(false);
          return;
        }

        const filterBindings = (configForm.filter_bindings || []).filter(isPersistableFilterBinding);

        const existingRow = layoutWidgets.find((w) => w.id === editingWidgetId);
        let prevCfg = existingRow?.config || {};
        if (typeof prevCfg === 'string') {
          try {
            prevCfg = JSON.parse(prevCfg);
          } catch {
            prevCfg = {};
          }
        }
        const showInDetailTabs = prevCfg.show_in_detail_tabs === false ? false : true;

        const updateData = {
          widget_label: configForm.widget_label,
          widget_column_position: configForm.widget_column_position,
          config: {
            search_view_id: configForm.search_view_id,
            search_view_name: configForm.search_view_name || undefined,
            filter_bindings: filterBindings,
            fields: selectedDisplayFields,
            max_records: configForm.maxRecords || 10,
            max_height: configForm.maxHeight || 400,
            display_as_table: configForm.display_as_table === true,
            show_in_detail_tabs: showInDetailTabs
          }
        };

        const api = new DataApi('layoutwidget');
        const response = await api.put(`/${editingWidgetId}`, updateData);

        if (response?.data?.success) {
          setSuccess('Search widget updated successfully');
          setShowConfigModal(false);
          setWidgetDraftKind(null);
          setEditingWidgetId(null);
          setSelectedRelatedList(null);
          await fetchLayoutWidgets();
          if (onSave) onSave();
        } else {
          setError('Failed to update widget: ' + (response?.data?.message || 'Unknown error'));
        }
        setLoading(false);
        return;
      }

      if (isMapWidget) {
        if (!editingWidgetId) {
          setError('Widget ID not found');
          setLoading(false);
          return;
        }

        const dataSource = configForm.map_data_source || 'record';
        const cfg = {
          data_source: dataSource,
          zoom: parseInt(configForm.map_zoom, 10) || 14,
          max_height: configForm.maxHeight || 400
        };

        if (dataSource === 'record') {
          const mode = configForm.map_coord_source_mode || 'combined';
          const fields = (configForm.map_address_fields || []).filter(Boolean);
          cfg.coord_source_mode = mode;
          if (mode === 'combined') {
            if (fields.length !== 1) {
              setError('Combined mode needs exactly 1 field');
              setLoading(false);
              return;
            }
            cfg.latlng_field = fields[0];
          } else if (mode === 'split') {
            if (fields.length !== 2) {
              setError('Separate Lat/Lng mode needs exactly 2 fields (lat first, lng second)');
              setLoading(false);
              return;
            }
            cfg.lat_field = fields[0];
            cfg.lng_field = fields[1];
          } else if (mode === 'address') {
            if (fields.length === 0) {
              setError('Address mode needs at least 1 field');
              setLoading(false);
              return;
            }
            cfg.address_fields = fields;
          }
        } else if (dataSource === 'route') {
          if (!configForm.map_route_related_list_id) {
            setError('Please select a related list for the route');
            setLoading(false);
            return;
          }
          const mode = configForm.map_route_coord_source_mode || 'combined';
          const fields = (configForm.map_route_address_fields || []).filter(Boolean);
          const route = {
            related_list_id: configForm.map_route_related_list_id,
            coord_source_mode: mode,
            max_stops: parseInt(configForm.map_route_max_stops, 10) || 25,
            connect: configForm.map_route_connect || 'polyline'
          };
          if (mode === 'combined') {
            if (fields.length !== 1) {
              setError('Combined mode needs exactly 1 child field');
              setLoading(false);
              return;
            }
            route.latlng_field = fields[0];
          } else if (mode === 'split') {
            if (fields.length !== 2) {
              setError('Separate Lat/Lng mode needs exactly 2 child fields (lat first, lng second)');
              setLoading(false);
              return;
            }
            route.lat_field = fields[0];
            route.lng_field = fields[1];
          } else if (mode === 'address') {
            if (fields.length === 0) {
              setError('Address mode needs at least 1 child field');
              setLoading(false);
              return;
            }
            route.address_fields = fields;
          }
          if (configForm.map_route_label_field) route.label_field = configForm.map_route_label_field;
          if (configForm.map_route_order_field) route.order_field = configForm.map_route_order_field;
          cfg.route = route;
        }

        const updateData = {
          widget_label: configForm.widget_label,
          widget_column_position: configForm.widget_column_position,
          config: cfg
        };

        const api = new DataApi('layoutwidget');
        const response = await api.put(`/${editingWidgetId}`, updateData);

        if (response?.data?.success) {
          setSuccess('Map widget updated successfully');
          setShowConfigModal(false);
          setWidgetDraftKind(null);
          setEditingWidgetId(null);
          setSelectedRelatedList(null);
          await fetchLayoutWidgets();
          if (onSave) onSave();
        } else {
          setError('Failed to update widget: ' + (response?.data?.message || 'Unknown error'));
        }
        setLoading(false);
        return;
      }

      // Check if this is Activity Timeline
      const isActivityTimeline = selectedRelatedList &&
                                 selectedRelatedList.target_object_name === 'activity' &&
                                 selectedRelatedList.source_field_name === 'id' &&
                                 selectedRelatedList.target_field_name === 'related_record_id';

      // Related list widget - existing validation (skip for Activity Timeline, Search and Map widgets)
      const isSearchWidgetUpdate = !selectedRelatedList && (editingWidgetId && layoutWidgets.find(w => w.id === editingWidgetId)?.widget_type === 'search');
      const isMapWidgetUpdate = !selectedRelatedList && (editingWidgetId && layoutWidgets.find(w => w.id === editingWidgetId)?.widget_type === 'map');
      if (!isActivityTimeline && !isSearchWidgetUpdate && !isMapWidgetUpdate) {
        // Validate: Maximum 4 fields
        if (configForm.fields.length > MAX_FIELDS_FOR_CARD) {
          setError(`Maximum ${MAX_FIELDS_FOR_CARD} fields allowed for card style display`);
          return;
        }

        if (configForm.fields.length === 0) {
          setError('Please select at least one field');
          return;
        }
      }

      if (!editingWidgetId) {
        setError('Widget ID not found');
        return;
      }

      // Use minimal fields for Activity Timeline (just 'id' for validation - Activity Timeline fetches its own fields)
      // For other widgets, use the selected fields
      const fieldsToUse = isActivityTimeline
        ? ['id'] // Minimal field set for Activity Timeline - it doesn't use widget config fields
        : configForm.fields;

      const updateData = {
        widget_label: configForm.widget_label,
        widget_column_position: configForm.widget_column_position,
        config: {
          fields: fieldsToUse,
          maxRecords: configForm.maxRecords,
          maxHeight: configForm.maxHeight,
          picklistField: isActivityTimeline ? null : (configForm.picklistField || null)
        }
      };

      const api = new DataApi('layoutwidget');
      const response = await api.put(`/${editingWidgetId}`, updateData);

      if (response?.data?.success) {
        setSuccess('Widget updated successfully');
        setShowConfigModal(false);
        setWidgetDraftKind(null);
        setEditingWidgetId(null);
        setSelectedRelatedList(null);
        await fetchLayoutWidgets(); // Wait for widgets to load
        fetchAvailableRelatedLists(); // Refresh available lists
        if (onSave) onSave();
      } else {
        setError('Failed to update widget: ' + (response?.data?.message || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error updating widget:', error);
      setError('Failed to update widget: ' + (error.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  const handleFieldToggle = (fieldName) => {
    const currentFields = configForm.fields || [];

    if (currentFields.includes(fieldName)) {
      // Remove field
      setConfigForm({
        ...configForm,
        fields: currentFields.filter(f => f !== fieldName)
      });
    } else {
      // Add field (max 4)
      if (currentFields.length >= MAX_FIELDS_FOR_CARD) {
        setError(`Maximum ${MAX_FIELDS_FOR_CARD} fields allowed for card style display`);
        return;
      }
      setConfigForm({
        ...configForm,
        fields: [...currentFields, fieldName]
      });
    }
  };

  const handleDragEnd = async (result) => {
    if (!result.destination) return;

    // Extract column number from droppableId (format: "column-1", "column-2", etc.)
    const sourceDroppableId = result.source.droppableId;
    const destDroppableId = result.destination.droppableId;

    // Only allow reordering within the same column
    if (sourceDroppableId !== destDroppableId) {
      return;
    }

    const columnNum = parseInt(sourceDroppableId.replace('column-', ''));

    // Get widgets for this column, sorted by display_order
    const columnWidgets = widgetsForManageColumns
      .filter(w => w.widget_column_position === columnNum)
      .sort((a, b) => (a.display_order || 0) - (b.display_order || 0));

    const items = Array.from(columnWidgets);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    // Update display_order for widgets in this column only
    const updates = items.map((item, index) => ({
      id: item.id,
      display_order: index
    }));

    try {
      setLoading(true);

      // Update all widgets in this column with new display order
      const api = new DataApi('layoutwidget');
      await Promise.all(
        updates.map(update =>
          api.put(`/${update.id}`, { display_order: update.display_order })
        )
      );

      fetchLayoutWidgets();
      if (onSave) onSave();
    } catch (error) {
      console.error('Error updating widget order:', error);
      setError('Failed to update widget order');
    } finally {
      setLoading(false);
    }
  };

  const handleFieldDragEnd = (result) => {
    if (!result.destination) return;

    const items = Array.from(configForm.fields);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    setConfigForm({
      ...configForm,
      fields: items
    });
  };


  // Show error if layoutId is missing
  if (!layoutId) {
    return (
      <Modal show={true} onHide={onClose} size="xl" className="layout-widget-manager-modal">
        <Modal.Header closeButton>
          <Modal.Title>
            <FaCog className="me-2" />
            Manage Layout Widgets
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="danger">
            <FaInfoCircle className="me-2" />
            Layout ID is missing. Please close this dialog and try again.
            <br />
            <small>Debug info: objectName={objectName}, layoutId={layoutId}</small>
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    );
  }

  return (
    <Modal show={true} onHide={onClose} size="xl" className="layout-widget-manager-modal">
      <Modal.Header closeButton>
        <Modal.Title>
          <FaCog className="me-2" />
          Manage Layout Widgets
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && (
          <Alert variant="danger" dismissible onClose={() => setError('')}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert variant="success" dismissible onClose={() => setSuccess('')}>
            {success}
          </Alert>
        )}

        {(() => {
          const totalColumns = layoutMetadata.total_columns || 3;
          const detailColumnPosition = layoutMetadata.detail_column_position || 1;

          // If layout has only 1 column, don't allow widgets
          if (totalColumns === 1) {
            return (
              <Alert variant="info">
                <FaInfoCircle className="me-2" />
                This layout has only 1 column which is reserved for the detail layout. Widgets cannot be added to single-column layouts.
              </Alert>
            );
          }

          // Calculate available widget columns (exclude detail column)
          const availableWidgetColumns = [];
          for (let i = 1; i <= totalColumns; i++) {
            if (i !== detailColumnPosition) {
              availableWidgetColumns.push(i);
            }
          }

          // Calculate column widths for right side
          // If 2 columns layout: Available (md=4), Column 1 (md=4), Column 2 (md=4)
          // If 3 columns layout: Available (md=3), Column 1 (md=3), Column 2 (md=3), Column 3 (md=3)
          const availableColWidth = totalColumns === 2 ? 4 : 3;
          const widgetColWidth = totalColumns === 2 ? 4 : 3;

          return (
            <Row>
              {/* Available Widgets - Left Side */}
              <Col md={availableColWidth}>
                <h5 className="mb-3">Available Widgets</h5>
                {loading && <p>Loading...</p>}

                {/* Special Widgets */}
                <div className="mb-3">
                  <h6 className="mb-2">Special Widgets</h6>

                  {/* Activity Timeline Widget */}
                  {!layoutWidgets.some(w =>
                    w.widget_type === 'activity' ||
                    (w.widget_type === 'relatedlist' && w.target_object_name === 'activity' &&
                     (w.related_list_label === 'Activity Timeline' || w.widget_label === 'Activity Timeline'))
                  ) && (
                    <ListGroup className="mb-2">
                      <ListGroup.Item
                        className="d-flex justify-content-between align-items-center"
                        style={{ cursor: 'pointer' }}
                        onClick={handleAddActivityWidget}
                      >
                        <div>
                          <strong>Activity Timeline</strong>
                          <br />
                          <small className="text-muted">
                            Display all activities (tasks, emails, notes) related to the record
                          </small>
                        </div>
                        <FaPlus className="text-primary" />
                      </ListGroup.Item>
                    </ListGroup>
                  )}

                  {/* Score Widget - Only show if scoring is active */}
                  {hasActiveScoringRule && !layoutWidgets.some(w => w.widget_type === 'score') && (
                    <ListGroup className="mb-2">
                      <ListGroup.Item
                        className="d-flex justify-content-between align-items-center"
                        style={{ cursor: 'pointer' }}
                        onClick={handleAddScoreWidget}
                      >
                        <div>
                          <strong>Lead Score</strong>
                          <br />
                          <small className="text-muted">
                            Display lead score with historical trend
                          </small>
                        </div>
                        <FaPlus className="text-primary" />
                      </ListGroup.Item>
                    </ListGroup>
                  )}

                  {/* Map Widget */}
                  <ListGroup className="mb-2">
                    <ListGroup.Item
                      className="d-flex justify-content-between align-items-center"
                      style={{ cursor: 'pointer' }}
                      onClick={handleAddMapWidget}
                    >
                      <div>
                        <strong>Map</strong>
                        <br />
                        <small className="text-muted">
                          Display a Google Map using lat/lng fields, a combined "lat, lng" field, or address fields
                        </small>
                      </div>
                      <FaPlus className="text-primary" />
                    </ListGroup.Item>
                  </ListGroup>
                </div>

                {/* Related lists + search widgets (same layout region as related data) */}
                <div className="mb-3">
                  <h6 className="mb-2">Related lists &amp; search</h6>
                  <p className="text-muted small mb-2">
                    Add a related list (card) or a search widget (search view with bindings to this record).
                  </p>
                  <ListGroup>
                    <ListGroup.Item
                      className="d-flex justify-content-between align-items-center"
                      style={{ cursor: 'pointer' }}
                      onClick={handleAddSearchWidget}
                    >
                      <div>
                        <strong>Search widget</strong>
                        <br />
                        <small className="text-muted">
                          Search results from a configured search view, filtered using fields on this record.
                          {availableSearchViews.length === 0
                            ? ' Define search views under Setup → Search Views first.'
                            : ''}
                        </small>
                      </div>
                      <FaPlus className="text-primary" />
                    </ListGroup.Item>
                    {availableRelatedLists.map((relatedList) => {
                      // Check if this is Activity Timeline and if it's already configured as a widget
                      const isActivityTimeline = relatedList.target_object_name === 'activity' &&
                                               relatedList.source_field_name === 'id' &&
                                               relatedList.target_field_name === 'related_record_id';
                      const isActivityWidgetConfigured = layoutWidgets.some(w =>
                        w.widget_type === 'activity' ||
                        (w.widget_type === 'relatedlist' && w.target_object_name === 'activity' &&
                         (w.related_list_label === 'Activity Timeline' || w.widget_label === 'Activity Timeline'))
                      );

                      // Hide Activity Timeline from Related Lists if it's already configured as a widget
                      if (isActivityTimeline && isActivityWidgetConfigured) {
                        return null;
                      }

                      return (
                        <ListGroup.Item
                          key={relatedList.id}
                          className="d-flex justify-content-between align-items-center"
                          style={{ cursor: 'pointer' }}
                          onClick={() => handleAddRelatedList(relatedList)}
                        >
                          <div>
                            <strong>{relatedList.relationship_label}</strong>
                            <br />
                            <small className="text-muted">
                              {relatedList.target_object_name}
                            </small>
                          </div>
                          <FaPlus className="text-primary" />
                        </ListGroup.Item>
                      );
                    })}
                  </ListGroup>
                  {!loading && availableRelatedLists.length === 0 && (
                    <p className="text-muted small mt-2 mb-0">
                      No additional related-list relationships found for this object. You can still add a search widget above.
                    </p>
                  )}
                </div>
              </Col>

              {/* Configured Widgets by Column - Right Side (sidebar search only; tab search lives in Related List Manager) */}
              <DragDropContext onDragEnd={handleDragEnd}>
                {availableWidgetColumns.map(colNum => {
                  const columnWidgets = widgetsForManageColumns
                    .filter(w => w.widget_column_position === colNum)
                    .sort((a, b) => (a.display_order || 0) - (b.display_order || 0));

                  const getColumnLabel = (colNum) => {
                    if (colNum === 1) return 'Column 1';
                    if (colNum === 2) return 'Column 2';
                    if (colNum === 3) return 'Column 3';
                    return `Column ${colNum}`;
                  };

                  return (
                    <Col key={colNum} md={widgetColWidth}>
                      <h5 className="mb-3">
                        {getColumnLabel(colNum)}
                        <Badge bg="secondary" className="ms-2">
                          {columnWidgets.length}
                        </Badge>
                      </h5>

                      {columnWidgets.length === 0 ? (
                        <p className="text-muted" style={{ fontStyle: 'italic', fontSize: '0.9rem' }}>
                          No widgets in this column
                        </p>
                      ) : (
                        <Droppable droppableId={`column-${colNum}`}>
                          {(provided) => (
                            <div {...provided.droppableProps} ref={provided.innerRef}>
                              {columnWidgets.map((widget, index) => (
                                <Draggable key={widget.id} draggableId={widget.id} index={index}>
                                  {(provided, snapshot) => (
                                    <Card
                                      ref={provided.innerRef}
                                      {...provided.draggableProps}
                                      className={`mb-2 ${snapshot.isDragging ? 'shadow' : ''}`}
                                    >
                                      <Card.Body>
                                        <div className="d-flex justify-content-between align-items-center">
                                          <div className="d-flex align-items-center">
                                            <div {...provided.dragHandleProps} className="me-2">
                                              <FaGripVertical />
                                            </div>
                                            <div>
                                              <strong>{widget.widget_label}</strong>
                                              <br />
                                              <small className="text-muted">
                                                {widget.widget_type === 'activity'
                                                  ? 'Activity Timeline'
                                                  : widget.widget_type === 'score'
                                                  ? 'Lead Score'
                                                  : widget.widget_type === 'search'
                                                  ? widget.config?.search_view_object_name || 'Search Widget'
                                                  : widget.widget_type === 'map'
                                                  ? `Map (${widget.config?.coord_source_mode || 'split'})`
                                                  : widget.related_list_label || widget.target_object_name}
                                              </small>
                                              <br />
                                              {widget.widget_type === 'activity' ? (
                                                <Badge bg="primary" className="me-1">Activity Widget</Badge>
                                              ) : widget.widget_type === 'score' ? (
                                                <Badge bg="success" className="me-1">Score Widget</Badge>
                                              ) : widget.widget_type === 'search' ? (
                                                <Badge bg="warning" className="me-1">Search Widget</Badge>
                                              ) : widget.widget_type === 'map' ? (
                                                <Badge bg="dark" className="me-1">Map Widget</Badge>
                                              ) : (
                                                <Badge bg="info" className="me-1">
                                                  {widget.config?.fields?.length || 0} fields
                                                </Badge>
                                              )}
                                            </div>
                                          </div>
                                          <div>
                                            <Button
                                              variant="outline-primary"
                                              size="sm"
                                              className="me-2"
                                              onClick={() => handleEditWidget(widget)}
                                            >
                                              <FaEdit />
                                            </Button>
                                            <Button
                                              variant="outline-danger"
                                              size="sm"
                                              onClick={() => handleDeleteWidget(widget.id)}
                                            >
                                              <FaTrash />
                                            </Button>
                                          </div>
                                        </div>
                                      </Card.Body>
                                    </Card>
                                  )}
                                </Draggable>
                              ))}
                              {provided.placeholder}
                            </div>
                          )}
                        </Droppable>
                      )}
                    </Col>
                  );
                })}
              </DragDropContext>
            </Row>
          );
        })()}

        {/* Configuration Modal */}
        <Modal
          show={showConfigModal}
          onHide={() => {
            setShowConfigModal(false);
            setEditingWidgetId(null);
            setWidgetDraftKind(null);
          }}
          size="xl"
          dialogClassName="layout-widget-config-modal"
        >
          <Modal.Header closeButton>
            <Modal.Title>
              <FaCog className="me-2" />
              {editingWidgetId ? 'Edit Widget' : 'Configure Widget'}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {selectedRelatedList ? (() => {
              // Check if this is Activity Timeline
              const isActivityTimeline = selectedRelatedList.target_object_name === 'activity' &&
                                       selectedRelatedList.source_field_name === 'id' &&
                                       selectedRelatedList.target_field_name === 'related_record_id';

              return (
                <Form onSubmit={(e) => e.preventDefault()}>
                  <Form.Group className="mb-3">
                    <Form.Label>Widget Label</Form.Label>
                    <Form.Control
                      type="text"
                      value={configForm.widget_label}
                      onChange={(e) => setConfigForm({
                        ...configForm,
                        widget_label: e.target.value
                      })}
                      placeholder="Enter widget label"
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Column Position</Form.Label>
                    <Form.Select
                      value={configForm.widget_column_position}
                      onChange={(e) => setConfigForm({
                        ...configForm,
                        widget_column_position: parseInt(e.target.value)
                      })}
                    >
                      {(() => {
                        const availablePositions = getAvailableWidgetPositions(layoutMetadata.total_columns, layoutMetadata.detail_column_position);
                        const positionLabels = {
                          1: 'Left (Column 1)',
                          2: 'Middle (Column 2)',
                          3: 'Right/Side (Column 3)'
                        };
                        return availablePositions.map(pos => (
                          <option key={pos} value={pos}>
                            {positionLabels[pos] || `Column ${pos}`}
                          </option>
                        ));
                      })()}
                    </Form.Select>
                    <Form.Text className="text-muted">
                      {(() => {
                        const detailPos = layoutMetadata.detail_column_position;
                        const totalCols = layoutMetadata.total_columns;
                        const availablePositions = getAvailableWidgetPositions(totalCols, detailPos);
                        const detailColumnLabel = detailPos === 1 ? 'Left (Column 1)' :
                                                   detailPos === 2 ? 'Middle (Column 2)' :
                                                   'Right/Side (Column 3)';
                        return `Detail layout is in ${detailColumnLabel}. ${availablePositions.length} ${availablePositions.length === 1 ? 'column is' : 'columns are'} available for widgets.`;
                      })()}
                    </Form.Text>
                  </Form.Group>

                  {/* Hide field selection for Activity Timeline */}
                  {!isActivityTimeline && (
                    <>
                      <Form.Group className="mb-3">
                        <Form.Label>
                          Fields for Card Display (Maximum {MAX_FIELDS_FOR_CARD})
                          <FaInfoCircle className="ms-2" title="Select up to 4 fields to display in card style" />
                        </Form.Label>
                        <div className="fields-selection">
                          {(selectedRelatedList.object_fields || []).map((field) => (
                            <Form.Check
                              key={field.field_name}
                              type="checkbox"
                              label={`${field.field_label} (${field.field_name})`}
                              checked={configForm.fields.includes(field.field_name)}
                              onChange={() => handleFieldToggle(field.field_name)}
                              disabled={!configForm.fields.includes(field.field_name) &&
                                configForm.fields.length >= MAX_FIELDS_FOR_CARD}
                            />
                          ))}
                        </div>
                        {configForm.fields.length > 0 && (
                          <div className="mt-2">
                            <strong>Selected Fields ({configForm.fields.length}/{MAX_FIELDS_FOR_CARD}) - Drag to reorder:</strong>
                            <DragDropContext onDragEnd={handleFieldDragEnd}>
                              <Droppable droppableId="selected-fields">
                                {(provided) => (
                                  <div
                                    {...provided.droppableProps}
                                    ref={provided.innerRef}
                                    className="selected-fields mt-2"
                                    style={{
                                      minHeight: '50px',
                                      padding: '10px',
                                      border: '1px solid #dee2e6',
                                      borderRadius: '4px',
                                      backgroundColor: '#f8f9fa'
                                    }}
                                  >
                                    {configForm.fields.map((fieldName, index) => {
                                      const field = selectedRelatedList.object_fields?.find(f => f.field_name === fieldName);
                                      return (
                                        <Draggable key={fieldName} draggableId={fieldName} index={index}>
                                          {(provided, snapshot) => (
                                            <div
                                              ref={provided.innerRef}
                                              {...provided.draggableProps}
                                              style={{
                                                ...provided.draggableProps.style,
                                                marginBottom: '8px',
                                                padding: '8px',
                                                backgroundColor: snapshot.isDragging ? '#e3f2fd' : 'white',
                                                border: '1px solid #dee2e6',
                                                borderRadius: '4px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '8px'
                                              }}
                                            >
                                              <div {...provided.dragHandleProps} style={{ cursor: 'grab' }}>
                                                <FaGripVertical className="text-muted" />
                                              </div>
                                              <Badge bg="primary" style={{ flex: 1 }}>
                                                {field?.field_label || fieldName}
                                              </Badge>
                                              <Button
                                                variant="link"
                                                size="sm"
                                                className="p-0"
                                                onClick={() => handleFieldToggle(fieldName)}
                                                style={{ color: '#dc3545', padding: '0 4px' }}
                                              >
                                                <FaTimes />
                                              </Button>
                                            </div>
                                          )}
                                        </Draggable>
                                      );
                                    })}
                                    {provided.placeholder}
                                  </div>
                                )}
                              </Droppable>
                            </DragDropContext>
                          </div>
                        )}
                      </Form.Group>

                      <Form.Group className="mb-3">
                        <Form.Label>Max Records</Form.Label>
                        <Form.Control
                          type="number"
                          value={configForm.maxRecords}
                          onChange={(e) => setConfigForm({
                            ...configForm,
                            maxRecords: Math.min(parseInt(e.target.value) || 10, 20)
                          })}
                          min="1"
                          max="20"
                        />
                        <Form.Text className="text-muted">
                          Maximum number of records to display in the widget (max: 20)
                        </Form.Text>
                      </Form.Group>

                      <Form.Group className="mb-3">
                        <Form.Label>
                          Group By Picklist Field (Optional)
                          <FaInfoCircle className="ms-2" title="Select a picklist field to display records as tabs" />
                        </Form.Label>
                        <Form.Select
                          value={configForm.picklistField}
                          onChange={(e) => setConfigForm({
                            ...configForm,
                            picklistField: e.target.value
                          })}
                        >
                          <option value="">None - Show all records</option>
                          {picklistFields.map((field) => (
                            <option key={field.field_name} value={field.field_name}>
                              {field.field_label} ({field.field_name})
                            </option>
                          ))}
                        </Form.Select>
                        <Form.Text className="text-muted">
                          Select a picklist field to group records into tabs (similar to Kanban view)
                        </Form.Text>
                      </Form.Group>
                    </>
                  )}

                  {/* For Activity Timeline, show Max Records with higher limit */}
                  {isActivityTimeline && (
                    <Form.Group className="mb-3">
                      <Form.Label>Max Records</Form.Label>
                      <Form.Control
                        type="number"
                        value={configForm.maxRecords}
                        onChange={(e) => setConfigForm({
                          ...configForm,
                          maxRecords: parseInt(e.target.value) || 100
                        })}
                        min="1"
                        max="200"
                      />
                      <Form.Text className="text-muted">
                        Maximum number of activity records to display in the timeline (max: 200)
                      </Form.Text>
                    </Form.Group>
                  )}

                  <Form.Group className="mb-3">
                    <Form.Label>Max Height (px)</Form.Label>
                    <Form.Control
                      type="number"
                      min="200"
                      max="1000"
                      step="50"
                      value={configForm.maxHeight}
                      onChange={(e) => setConfigForm({
                        ...configForm,
                        maxHeight: parseInt(e.target.value) || (isActivityTimeline ? 600 : 400)
                      })}
                      placeholder="Maximum height in pixels"
                    />
                    <Form.Text className="text-muted">
                      Maximum height of the widget card in pixels (200-1000). Content will scroll if it exceeds this height.
                    </Form.Text>
                  </Form.Group>

                  {isActivityTimeline && (
                    <Alert variant="info">
                      <FaInfoCircle className="me-2" />
                      The Activity Timeline widget displays all activities (tasks, emails, notes) related to the current record. Fields are automatically configured.
                    </Alert>
                  )}
                </Form>
              );
            })() : (() => {
              const isScoreWidget = widgetDraftKind === 'score';
              const isActivityWidget = widgetDraftKind === 'activity';
              const isSearchWidget = widgetDraftKind === 'search';
              const isMapWidget = widgetDraftKind === 'map';

              return (
              <Form onSubmit={(e) => e.preventDefault()}>
                <Form.Group className="mb-3">
                  <Form.Label>Widget Label</Form.Label>
                  <Form.Control
                    type="text"
                    value={configForm.widget_label}
                    onChange={(e) => setConfigForm({
                      ...configForm,
                      widget_label: e.target.value
                    })}
                    placeholder={isScoreWidget ? "Lead Score" : "Activity Timeline"}
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Column Position</Form.Label>
                  <Form.Select
                    value={configForm.widget_column_position}
                    onChange={(e) => setConfigForm({
                      ...configForm,
                      widget_column_position: parseInt(e.target.value)
                    })}
                  >
                    {(() => {
                      const availablePositions = getAvailableWidgetPositions(layoutMetadata.total_columns, layoutMetadata.detail_column_position);
                      const positionLabels = {
                        1: 'Left (Column 1)',
                        2: 'Middle (Column 2)',
                        3: 'Right/Side (Column 3)'
                      };
                      return availablePositions.map(pos => (
                        <option key={pos} value={pos}>
                          {positionLabels[pos] || `Column ${pos}`}
                        </option>
                      ));
                    })()}
                  </Form.Select>
                  <Form.Text className="text-muted">
                    {(() => {
                      const detailPos = layoutMetadata.detail_column_position;
                      const totalCols = layoutMetadata.total_columns;
                      const availablePositions = getAvailableWidgetPositions(totalCols, detailPos);
                      const detailColumnLabel = detailPos === 1 ? 'Left (Column 1)' :
                                                 detailPos === 2 ? 'Middle (Column 2)' :
                                                 'Right/Side (Column 3)';
                      return `Detail layout is in ${detailColumnLabel}. ${availablePositions.length} ${availablePositions.length === 1 ? 'column is' : 'columns are'} available for widgets.`;
                    })()}
                  </Form.Text>
                </Form.Group>

                {(isActivityWidget || isScoreWidget || isMapWidget) && (
                  <Form.Group className="mb-3">
                    <Form.Label>Max Height (pixels)</Form.Label>
                    <Form.Control
                      type="number"
                      value={configForm.maxHeight}
                      onChange={(e) => setConfigForm({
                        ...configForm,
                        maxHeight: parseInt(e.target.value) || (isActivityWidget ? 600 : 400)
                      })}
                      min="200"
                      max="1000"
                    />
                    <Form.Text className="text-muted">
                      {isActivityWidget
                        ? 'Maximum height for the activity timeline widget'
                        : isMapWidget
                        ? 'Maximum height of the map widget card.'
                        : 'Maximum height of the Lead Score widget card. Content will scroll if it exceeds this height.'}
                    </Form.Text>
                  </Form.Group>
                )}

                {isMapWidget && (() => {
                  const dataSource = configForm.map_data_source || 'record';

                  // Field options for THIS record (record mode)
                  const fieldOptions = sourceObjectFields || [];
                  const filterNumericLike = (f) => {
                    const t = (f.data_type || '').toLowerCase();
                    return t === '' || t === 'number' || t === 'decimal' || t === 'currency' ||
                      t === 'text' || t === 'string' || t === 'integer';
                  };
                  const filterTextLike = (f) => {
                    const t = (f.data_type || '').toLowerCase();
                    return t === '' || t === 'text' || t === 'string' || t === 'textarea';
                  };
                  // Stricter allow-list for address-field pickers — only the
                  // types that produce a clean human-readable string suitable
                  // for geocoding.
                  const filterAddressLike = (f) => {
                    const t = (f.data_type || '').toLowerCase();
                    return (
                      t === 'text' ||
                      t === 'string' ||
                      t === 'textarea' ||
                      t === 'picklist' ||
                      t === 'multipicklist' ||
                      t === 'multi_picklist' ||
                      t === 'quick_search' ||
                      t === 'quicksearch' ||
                      t === 'display' ||
                      t === 'displayfield' ||
                      t === 'display_field'
                    );
                  };
                  // Note: numericLike / textLike / addressLike are computed
                  // per-shuttle (below) based on the current selection count, so
                  // the Available list reflects only types valid for the next pick.

                  // Field options for CHILD records (route mode)
                  const pickedRL = (allRelatedLists || []).find(
                    rl => String(rl.id) === String(configForm.map_route_related_list_id)
                  );
                  const childFieldsRaw = pickedRL?.object_fields || [];
                  const childFields = childFieldsRaw.map(cf => ({
                    field_name: cf.field_name,
                    field_label: cf.field_label || cf.field_name,
                    data_type: cf.data_type
                  }));

                  /**
                   * Two-list (shuttle) picker for ordered address fields, styled
                   * like EnhancedListViewDialog's selected-fields panel:
                   *   left  -> available fields (click "+" to add)
                   *   right -> selected fields, drag to reorder, trash to remove
                   * If `maxFields` is given, adding more is disabled once the cap
                   * is reached.
                   */
                  const renderAddressShuttle = (availableFields, selected, onChange, maxFields = null) => {
                    const selectedSet = new Set(selected || []);
                    const available = (availableFields || []).filter(f => !selectedSet.has(f.field_name));
                    const fieldMap = new Map(
                      (availableFields || []).map(f => [f.field_name, f])
                    );
                    const atCap = maxFields != null && (selected || []).length >= maxFields;

                    const add = (name) => {
                      if (atCap) return;
                      onChange([...(selected || []), name]);
                    };
                    const remove = (name) => onChange((selected || []).filter(x => x !== name));

                    const handleDragEnd = (result) => {
                      if (!result.destination) return;
                      const from = result.source.index;
                      const to = result.destination.index;
                      if (from === to) return;
                      const cur = Array.from(selected || []);
                      const [moved] = cur.splice(from, 1);
                      cur.splice(to, 0, moved);
                      onChange(cur);
                    };

                    const panelStyle = {
                      flex: 1,
                      minWidth: 0,
                      maxHeight: 400,
                      overflowY: 'auto',
                      padding: 12,
                      backgroundColor: '#f8f9fa',
                      border: '1px solid #dee2e6',
                      borderRadius: 4
                    };

                    return (
                      <Row className="g-2">
                        {/* Available column */}
                        <Col md={6}>
                          <div className="d-flex justify-content-between align-items-center mb-2">
                            <h6 className="mb-0" style={{ fontSize: '0.85rem' }}>
                              <Badge bg="secondary" className="me-2">{available.length}</Badge>
                              Available Fields
                            </h6>
                            <small className="text-muted">Click <FaPlus style={{ fontSize: '0.7rem' }} /> to add</small>
                          </div>
                          <div style={panelStyle}>
                            {atCap && (
                              <Alert variant="warning" className="py-2 px-3 mb-2" style={{ fontSize: '12px' }}>
                                <strong>Limit reached.</strong> Remove a selected field to add a different one.
                              </Alert>
                            )}
                            {available.length === 0 ? (
                              <div className="text-center text-muted py-4">
                                <p className="mb-0 small">All matching fields are selected.</p>
                              </div>
                            ) : available.map((f) => (
                              <div
                                key={f.field_name}
                                onClick={() => add(f.field_name)}
                                className="d-flex justify-content-between align-items-center p-2 mb-2 bg-white rounded border"
                                style={{
                                  cursor: atCap ? 'not-allowed' : 'pointer',
                                  opacity: atCap ? 0.55 : 1,
                                  transition: 'all 0.15s ease'
                                }}
                                onMouseEnter={(e) => { if (!atCap) e.currentTarget.style.borderColor = '#1f6feb'; }}
                                onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#dee2e6'; }}
                                title={atCap ? 'Selection limit reached' : 'Add to address sequence'}
                              >
                                <div style={{ overflow: 'hidden' }}>
                                  <div className="fw-bold" style={{ fontSize: '0.875rem' }}>
                                    {f.field_label || f.field_name}
                                  </div>
                                  <small className="text-muted">{f.field_name}</small>
                                </div>
                                <Button
                                  variant="outline-primary"
                                  size="sm"
                                  className="py-0 px-2"
                                  disabled={atCap}
                                  onClick={(e) => { e.stopPropagation(); add(f.field_name); }}
                                  title={atCap ? 'Selection limit reached' : 'Add'}
                                >
                                  <FaPlus style={{ fontSize: '0.7rem' }} />
                                </Button>
                              </div>
                            ))}
                          </div>
                        </Col>

                        {/* Selected column — drag to reorder */}
                        <Col md={6}>
                          <div className="d-flex justify-content-between align-items-center mb-2">
                            <h6 className="mb-0" style={{ fontSize: '0.85rem' }}>
                              <Badge bg={(selected || []).length === 0 ? 'secondary' : 'primary'} className="me-2">
                                {(selected || []).length}
                              </Badge>
                              Address Sequence
                            </h6>
                            <small className="text-muted">Drag to reorder</small>
                          </div>
                          <div style={panelStyle}>
                            {(selected || []).length === 0 ? (
                              <div className="text-center text-muted py-4">
                                <FaGripVertical className="mb-2" size={20} />
                                <p className="mb-0 small">No fields selected</p>
                                <small>Add fields from the left, then drag to set order</small>
                              </div>
                            ) : (
                              <DragDropContext onDragEnd={handleDragEnd}>
                                <Droppable droppableId="addr-shuttle">
                                  {(dropProvided) => (
                                    <div ref={dropProvided.innerRef} {...dropProvided.droppableProps}>
                                      {(selected || []).map((name, index) => {
                                        const f = fieldMap.get(name) || { field_name: name, field_label: name };
                                        return (
                                          <Draggable key={name} draggableId={name} index={index}>
                                            {(provided, snapshot) => (
                                              <div
                                                ref={provided.innerRef}
                                                {...provided.draggableProps}
                                                className="d-flex justify-content-between align-items-center p-2 mb-2 bg-white rounded border"
                                                style={{
                                                  ...provided.draggableProps.style,
                                                  borderColor: snapshot.isDragging ? '#1f6feb' : '#dee2e6',
                                                  boxShadow: snapshot.isDragging ? '0 6px 12px rgba(0,0,0,0.15)' : 'none'
                                                }}
                                              >
                                                <div
                                                  className="d-flex align-items-center gap-2"
                                                  style={{ minWidth: 0, flex: 1 }}
                                                >
                                                  <div
                                                    {...provided.dragHandleProps}
                                                    style={{ cursor: 'grab', flexShrink: 0 }}
                                                    title={`#${index + 1} — drag to reorder`}
                                                  >
                                                    <FaGripVertical className="text-muted" />
                                                  </div>
                                                  <div style={{ minWidth: 0, overflow: 'hidden', flex: 1 }}>
                                                    <div className="fw-bold text-truncate" style={{ fontSize: '0.875rem' }}>
                                                      {f.field_label || f.field_name}
                                                    </div>
                                                    <small className="text-muted text-truncate d-block">{f.field_name}</small>
                                                  </div>
                                                </div>
                                                <Button
                                                  variant="outline-danger"
                                                  size="sm"
                                                  className="py-0 px-2"
                                                  style={{ flexShrink: 0 }}
                                                  onClick={() => remove(name)}
                                                  title="Remove field"
                                                >
                                                  <FaTrash style={{ fontSize: '0.7rem' }} />
                                                </Button>
                                              </div>
                                            )}
                                          </Draggable>
                                        );
                                      })}
                                      {dropProvided.placeholder}
                                    </div>
                                  )}
                                </Droppable>
                              </DragDropContext>
                            )}
                          </div>
                        </Col>
                      </Row>
                    );
                  };

                  return (
                    <>
                      {/* Data source picker — single record vs child route */}
                      <Form.Group className="mb-3">
                        <Form.Label>Data Source</Form.Label>
                        <Form.Select
                          value={dataSource}
                          onChange={(e) => setConfigForm({
                            ...configForm,
                            map_data_source: e.target.value
                          })}
                        >
                          <option value="record">This record (single pin)</option>
                          <option value="route">Child records (route / multiple stops)</option>
                        </Form.Select>
                        <Form.Text className="text-muted">
                          Choose what to plot: just this record, or a list of child records (e.g. visit stops on a visit plan).
                        </Form.Text>
                      </Form.Group>

                      {dataSource === 'record' && (() => {
                        const mode = configForm.map_coord_source_mode || 'combined';
                        const picked = configForm.map_address_fields || [];
                        // Available list + selection cap depend on the chosen mode:
                        //   combined -> 1 field, text only
                        //   split    -> 2 fields, number + text
                        //   address  -> 3+ fields, address-like types
                        let availForRecord;
                        let maxFields;
                        let modeHelp;
                        if (mode === 'combined') {
                          availForRecord = fieldOptions.filter(filterTextLike);
                          maxFields = 1;
                          modeHelp = 'Pick one text field containing a value like "28.6129, 77.2295".';
                        } else if (mode === 'split') {
                          availForRecord = fieldOptions.filter(filterNumericLike);
                          maxFields = 2;
                          modeHelp = 'Pick exactly two fields — first is latitude, second is longitude.';
                        } else {
                          availForRecord = fieldOptions.filter(filterAddressLike);
                          maxFields = null;
                          modeHelp = 'Pick the address parts in the order you want them joined (e.g. street, city, state, country).';
                        }
                        return (
                          <>
                            <Form.Group className="mb-3">
                              <Form.Label>Coordinate Source</Form.Label>
                              <Form.Select
                                value={mode}
                                onChange={(e) => {
                                  const next = e.target.value;
                                  // Trim selected fields to the new cap when switching down
                                  let nextFields = configForm.map_address_fields || [];
                                  if (next === 'combined') nextFields = nextFields.slice(0, 1);
                                  else if (next === 'split') nextFields = nextFields.slice(0, 2);
                                  setConfigForm({
                                    ...configForm,
                                    map_coord_source_mode: next,
                                    map_address_fields: nextFields
                                  });
                                }}
                              >
                                <option value="combined">1 field — Combined "lat, lng"</option>
                                <option value="split">2 fields — Separate Lat &amp; Lng</option>
                                <option value="address">3+ fields — Address (geocoded)</option>
                              </Form.Select>
                              <Form.Text className="text-muted">
                                Drives which fields are available below and how the map reads the location.
                              </Form.Text>
                            </Form.Group>

                            <Form.Group className="mb-3">
                              <Form.Label>
                                Address Fields (joined in sequence before geocoding)
                                {maxFields && (
                                  <Badge bg={picked.length === maxFields ? 'success' : 'secondary'} className="ms-2">
                                    {picked.length} / {maxFields}
                                  </Badge>
                                )}
                              </Form.Label>
                              {renderAddressShuttle(
                                availForRecord,
                                picked,
                                (next) => setConfigForm({ ...configForm, map_address_fields: next }),
                                maxFields
                              )}
                              <Form.Text className="text-muted">{modeHelp}</Form.Text>
                            </Form.Group>
                          </>
                        );
                      })()}

                      {dataSource === 'route' && (
                        <>
                          <Form.Group className="mb-3">
                            <Form.Label>Child Related List</Form.Label>
                            <Form.Select
                              value={configForm.map_route_related_list_id}
                              onChange={(e) => setConfigForm({
                                ...configForm,
                                map_route_related_list_id: e.target.value,
                                // Reset child-field selections when relationship changes
                                map_route_lat_field: '',
                                map_route_lng_field: '',
                                map_route_latlng_field: '',
                                map_route_address_fields: [],
                                map_route_label_field: '',
                                map_route_order_field: ''
                              })}
                            >
                              <option value="">Select a child relationship...</option>
                              {(allRelatedLists || []).map(rl => (
                                <option key={rl.id} value={rl.id}>
                                  {rl.relationship_label || rl.target_object_name} → {rl.target_object_name}
                                </option>
                              ))}
                            </Form.Select>
                            <Form.Text className="text-muted">
                              Records from this child relationship will be plotted as stops.
                            </Form.Text>
                          </Form.Group>

                          {configForm.map_route_related_list_id && (() => {
                            const mode = configForm.map_route_coord_source_mode || 'combined';
                            const picked = configForm.map_route_address_fields || [];
                            let availForRoute;
                            let maxFields;
                            let modeHelp;
                            if (mode === 'combined') {
                              availForRoute = childFields.filter(filterTextLike);
                              maxFields = 1;
                              modeHelp = 'Pick one text field on the child containing a value like "28.6129, 77.2295".';
                            } else if (mode === 'split') {
                              availForRoute = childFields.filter(filterNumericLike);
                              maxFields = 2;
                              modeHelp = 'Pick exactly two child fields — first is latitude, second is longitude.';
                            } else {
                              availForRoute = childFields.filter(filterAddressLike);
                              maxFields = null;
                              modeHelp = 'Pick the address parts on the child in the order to join them (e.g. street, city, state, country).';
                            }
                            return (
                              <>
                                <Form.Group className="mb-3">
                                  <Form.Label>Coordinate Source on Child</Form.Label>
                                  <Form.Select
                                    value={mode}
                                    onChange={(e) => {
                                      const next = e.target.value;
                                      let nextFields = configForm.map_route_address_fields || [];
                                      if (next === 'combined') nextFields = nextFields.slice(0, 1);
                                      else if (next === 'split') nextFields = nextFields.slice(0, 2);
                                      setConfigForm({
                                        ...configForm,
                                        map_route_coord_source_mode: next,
                                        map_route_address_fields: nextFields
                                      });
                                    }}
                                  >
                                    <option value="combined">1 field — Combined "lat, lng"</option>
                                    <option value="split">2 fields — Separate Lat &amp; Lng</option>
                                    <option value="address">3+ fields — Address (geocoded)</option>
                                  </Form.Select>
                                </Form.Group>

                                <Form.Group className="mb-3">
                                  <Form.Label>
                                    Address Fields on Child (joined in sequence before geocoding)
                                    {maxFields && (
                                      <Badge bg={picked.length === maxFields ? 'success' : 'secondary'} className="ms-2">
                                        {picked.length} / {maxFields}
                                      </Badge>
                                    )}
                                  </Form.Label>
                                  {renderAddressShuttle(
                                    availForRoute,
                                    picked,
                                    (next) => setConfigForm({ ...configForm, map_route_address_fields: next }),
                                    maxFields
                                  )}
                                  <Form.Text className="text-muted">{modeHelp}</Form.Text>
                                </Form.Group>
                              </>
                            );
                          })()}

                          {configForm.map_route_related_list_id && (
                            <>

                              <Row className="mb-3">
                                <Col md={6}>
                                  <Form.Label>Stop Label Field (optional)</Form.Label>
                                  <Form.Select
                                    value={configForm.map_route_label_field}
                                    onChange={(e) => setConfigForm({
                                      ...configForm,
                                      map_route_label_field: e.target.value
                                    })}
                                  >
                                    <option value="">— None —</option>
                                    {childFields.map(f => (
                                      <option key={f.field_name} value={f.field_name}>
                                        {f.field_label} ({f.field_name})
                                      </option>
                                    ))}
                                  </Form.Select>
                                  <Form.Text className="text-muted">
                                    Shown in the marker's info window.
                                  </Form.Text>
                                </Col>
                                <Col md={6}>
                                  <Form.Label>Order By (optional)</Form.Label>
                                  <Form.Select
                                    value={configForm.map_route_order_field}
                                    onChange={(e) => setConfigForm({
                                      ...configForm,
                                      map_route_order_field: e.target.value
                                    })}
                                  >
                                    <option value="">— Created date (default) —</option>
                                    {childFields.map(f => (
                                      <option key={f.field_name} value={f.field_name}>
                                        {f.field_label} ({f.field_name})
                                      </option>
                                    ))}
                                  </Form.Select>
                                  <Form.Text className="text-muted">
                                    Stops are plotted in ascending order of this field.
                                  </Form.Text>
                                </Col>
                              </Row>

                              <Row className="mb-3">
                                <Col md={6}>
                                  <Form.Label>Max Stops</Form.Label>
                                  <Form.Control
                                    type="number"
                                    min="1"
                                    max="100"
                                    value={configForm.map_route_max_stops}
                                    onChange={(e) => setConfigForm({
                                      ...configForm,
                                      map_route_max_stops: parseInt(e.target.value, 10) || 25
                                    })}
                                  />
                                </Col>
                                <Col md={6}>
                                  <Form.Label>Connect Stops</Form.Label>
                                  <Form.Select
                                    value={configForm.map_route_connect || 'polyline'}
                                    onChange={(e) => setConfigForm({
                                      ...configForm,
                                      map_route_connect: e.target.value
                                    })}
                                  >
                                    <option value="none">None — just pins</option>
                                    <option value="polyline">Straight line (free)</option>
                                    <option value="directions">Driving route (Google Directions API)</option>
                                  </Form.Select>
                                </Col>
                              </Row>
                            </>
                          )}
                        </>
                      )}

                      <Form.Group className="mb-3">
                        <Form.Label>Default Zoom (1–20)</Form.Label>
                        <Form.Control
                          type="number"
                          min="1"
                          max="20"
                          value={configForm.map_zoom}
                          onChange={(e) => setConfigForm({
                            ...configForm,
                            map_zoom: parseInt(e.target.value) || 14
                          })}
                        />
                        <Form.Text className="text-muted">
                          Route mode auto-fits the map to all stops; this is the zoom for single-record mode.
                        </Form.Text>
                      </Form.Group>

                      <Alert variant="info">
                        <FaInfoCircle className="me-2" />
                        The Map widget displays a Google Map. Address mode uses the existing GeoService (requires <code>GOOGLE_MAPS_API_KEY</code>). The driving-route option will consume Google Directions API quota.
                      </Alert>
                    </>
                  );
                })()}

                {isActivityWidget && (
                  <Alert variant="info">
                    <FaInfoCircle className="me-2" />
                    The Activity Timeline widget displays all activities (tasks, emails, notes) related to the current record.
                  </Alert>
                )}

                {isScoreWidget && (
                  <Alert variant="info">
                    <FaInfoCircle className="me-2" />
                    The Lead Score widget displays the lead score with historical trend visualization. This widget is only available when scoring rules are active for this object.
                  </Alert>
                )}

                {isSearchWidget && (
                  <>
                    <Form.Group className="mb-3">
                      <Form.Label>Search View</Form.Label>
                      <Form.Select
                        value={configForm.search_view_id}
                        onChange={(e) => {
                          const newSearchViewId = e.target.value;
                          const previousSearchViewId = configForm.search_view_id;

                          // Save current configuration for the previous search view before switching
                          if (previousSearchViewId) {
                            setSearchViewConfigsCache(prev => ({
                              ...prev,
                              [previousSearchViewId]: {
                                fields: configForm.fields || [],
                                filter_bindings: configForm.filter_bindings || []
                              }
                            }));
                            console.log('📋 [LayoutWidgetManager] Saved config for search view:', previousSearchViewId, {
                              fields: configForm.fields,
                              filter_bindings: configForm.filter_bindings
                            });
                          }

                          // Check if we have cached config for the new search view
                          const cachedConfig = searchViewConfigsCache[newSearchViewId];
                          const picked = availableSearchViews.find((s) => String(s.id) === String(newSearchViewId));

                          // Switch to new search view
                          setConfigForm({
                            ...configForm,
                            search_view_id: newSearchViewId,
                            search_view_name: picked?.search_view_name || '',
                            // Restore cached config if available, otherwise reset
                            fields: cachedConfig?.fields || [],
                            filter_bindings: cachedConfig?.filter_bindings || []
                          });

                          if (cachedConfig) {
                            console.log('📋 [LayoutWidgetManager] Restoring cached config for search view:', newSearchViewId, cachedConfig);
                          } else {
                            console.log('📋 [LayoutWidgetManager] No cached config for search view:', newSearchViewId, '- will initialize defaults');
                          }
                        }}
                      >
                        <option value="">Select a search view...</option>
                        {availableSearchViews.map((sv) => {
                          const objTitle = sv.object_label || sv.object_name || '';
                          const viewLbl = sv.view_label || '—';
                          const slug = sv.search_view_name || '—';
                          return (
                            <option key={sv.id} value={sv.id}>
                              {`${objTitle} (${viewLbl} - ${slug})`}
                            </option>
                          );
                        })}
                      </Form.Select>
                      <Form.Text className="text-muted">
                        Select a configured search view. Search views are configured in Setup.
                      </Form.Text>
                    </Form.Group>

                    {configForm.search_view_id && (
                      <>
                        <Form.Group className="mb-3">
                          <Form.Label>Fields to Display</Form.Label>
                          <Form.Text className="text-muted d-block mb-2">
                            Choose fields for this search widget (up to{' '}
                            {Math.min(
                              searchViewFields.length || MAX_SEARCH_WIDGET_DISPLAY_FIELDS,
                              MAX_SEARCH_WIDGET_DISPLAY_FIELDS
                            )}{' '}
                            from this view, max {MAX_SEARCH_WIDGET_DISPLAY_FIELDS} overall). Use Available / Selected;
                            drag Selected to reorder.
                          </Form.Text>
                          {(() => {
                            if (configForm.search_view_id && searchViewFields.length === 0 && filterableFields.length === 0 && !searchViewConfig) {
                              return <p className="text-muted">Loading fields from search view...</p>;
                            }

                            if (searchViewFields.length > 0) {
                              const selectedNames = configForm.fields || [];
                              const searchFieldCap = Math.min(
                                searchViewFields.length,
                                MAX_SEARCH_WIDGET_DISPLAY_FIELDS
                              );
                              const availableList = searchViewFields.filter(
                                (f) => !selectedNames.includes(f.field_name)
                              );
                              return (
                                <Row className="g-3">
                                  <Col md={6}>
                                    <div
                                      className="border rounded p-3 h-100 bg-white"
                                      style={{ minHeight: 220 }}
                                    >
                                      <div className="fw-semibold mb-1">Available fields</div>
                                      <small className="text-muted d-block mb-2">
                                        All fields from this search view ({searchViewFields.length}).
                                      </small>
                                      <div style={{ maxHeight: 320, overflowY: 'auto' }}>
                                        {availableList.length === 0 ? (
                                          <p className="text-muted small mb-0">
                                            {selectedNames.length >= searchFieldCap
                                              ? `Maximum ${searchFieldCap} selected. Remove one to add another.`
                                              : 'All display fields are selected.'}
                                          </p>
                                        ) : (
                                          availableList.map((field) => (
                                            <div
                                              key={field.field_name}
                                              className="d-flex justify-content-between align-items-center py-2 border-bottom"
                                            >
                                              <div className="me-2" style={{ minWidth: 0 }}>
                                                <div className="text-truncate">{field.field_label}</div>
                                                <small className="text-muted">{field.field_name}</small>
                                              </div>
                                              <Button
                                                type="button"
                                                size="sm"
                                                variant="outline-primary"
                                                className="flex-shrink-0"
                                                title="Add to selected"
                                                disabled={selectedNames.length >= searchFieldCap}
                                                onClick={() => {
                                                  if (selectedNames.length >= searchFieldCap) {
                                                    setError(`Maximum ${searchFieldCap} fields allowed`);
                                                    return;
                                                  }
                                                  addSearchWidgetDisplayField(field.field_name);
                                                }}
                                              >
                                                <FaPlus />
                                              </Button>
                                            </div>
                                          ))
                                        )}
                                      </div>
                                    </div>
                                  </Col>
                                  <Col md={6}>
                                    <div
                                      className="border rounded p-3 h-100"
                                      style={{ minHeight: 220, backgroundColor: '#f8f9fa' }}
                                    >
                                      <div className="fw-semibold mb-1">
                                        Selected fields ({selectedNames.length}/{searchFieldCap})
                                      </div>
                                      <small className="text-muted d-block mb-2">
                                        Shown on the widget in this order.
                                      </small>
                                      {selectedNames.length === 0 ? (
                                        <p className="text-muted small mb-0">Add fields from the left.</p>
                                      ) : (
                                        <DragDropContext onDragEnd={handleSearchWidgetSelectedFieldsDragEnd}>
                                          <Droppable droppableId="lw-search-selected-fields">
                                            {(dropProvided) => (
                                              <div
                                                ref={dropProvided.innerRef}
                                                {...dropProvided.droppableProps}
                                                style={{ minHeight: 48 }}
                                              >
                                                {selectedNames.map((fieldName, index) => {
                                                  const fieldMeta = searchViewFields.find(
                                                    (f) => f.field_name === fieldName
                                                  );
                                                  const label = fieldMeta?.field_label || fieldName;
                                                  return (
                                                    <Draggable
                                                      key={fieldName}
                                                      draggableId={`lw-search-sel-${fieldName}`}
                                                      index={index}
                                                    >
                                                      {(dragProvided, snapshot) => (
                                                        <div
                                                          ref={dragProvided.innerRef}
                                                          {...dragProvided.draggableProps}
                                                          className={`d-flex align-items-center gap-2 mb-2 p-2 bg-white border rounded ${
                                                            snapshot.isDragging ? 'shadow-sm' : ''
                                                          }`}
                                                        >
                                                          <div
                                                            {...dragProvided.dragHandleProps}
                                                            className="text-muted"
                                                            style={{ cursor: 'grab' }}
                                                          >
                                                            <FaGripVertical />
                                                          </div>
                                                          <div className="flex-grow-1" style={{ minWidth: 0 }}>
                                                            <div className="text-truncate">{label}</div>
                                                            <small className="text-muted">{fieldName}</small>
                                                          </div>
                                                          <Button
                                                            type="button"
                                                            variant="link"
                                                            className="p-0 text-danger flex-shrink-0"
                                                            onClick={() => removeSearchWidgetDisplayField(fieldName)}
                                                          >
                                                            <FaTimes />
                                                          </Button>
                                                        </div>
                                                      )}
                                                    </Draggable>
                                                  );
                                                })}
                                                {dropProvided.placeholder}
                                              </div>
                                            )}
                                          </Droppable>
                                        </DragDropContext>
                                      )}
                                    </div>
                                  </Col>
                                </Row>
                              );
                            }

                            if (searchViewConfig && searchViewFields.length === 0) {
                              return (
                                <Alert variant="warning" className="mb-0">
                                  <FaInfoCircle className="me-2" />
                                  No display fields configured in the search view. Please configure the search view in Setup.
                                </Alert>
                              );
                            }

                            return <p className="text-muted">Select a search view above to see available fields.</p>;
                          })()}
                        </Form.Group>

                        <Form.Group className="mb-3">
                          <Form.Label>Filter Field Bindings</Form.Label>
                          <Form.Text className="text-muted d-block mb-2">
                            Bind filterable fields from the search view to fields from the current record (e.g., {'{{lead.email}}'})
                          </Form.Text>
                          {filterableFields.length > 0 ? (
                            filterableFields.map((filterField) => {
                              const binding = (configForm.filter_bindings || []).find(
                                b => b.filterable_field === filterField.field_name
                              ) || {
                                filterable_field: filterField.field_name,
                                source_field: '',
                                operator: defaultOperatorForDataType(filterField.data_type)
                              };
                              const opValue = effectiveFilterBindingOperator(binding);
                              const operatorOpts = getOperatorsForDataType(filterField.data_type);

                              return (
                                <Row key={filterField.field_name} className="mb-2 align-items-end">
                                  <Col md={4} className="mb-2 mb-md-0">
                                    <Form.Label className="mb-0">
                                      <strong>{filterField.field_label}</strong>
                                      <br />
                                      <small className="text-muted">({filterField.field_name})</small>
                                    </Form.Label>
                                  </Col>
                                  <Col md={3} className="mb-2 mb-md-0">
                                    <Form.Label className="small text-muted mb-1">Operator</Form.Label>
                                    <Form.Select
                                      value={opValue}
                                      onChange={(e) => {
                                        const newOp = e.target.value;
                                        const newBindings = [...(configForm.filter_bindings || [])];
                                        const existingIndex = newBindings.findIndex(
                                          b => b.filterable_field === filterField.field_name
                                        );
                                        const patch = {
                                          filterable_field: filterField.field_name,
                                          source_field: binding.source_field || '',
                                          operator: newOp
                                        };
                                        if (!filterBindingRequiresSourceField(newOp)) {
                                          patch.source_field = '';
                                        }
                                        if (existingIndex >= 0) {
                                          newBindings[existingIndex] = {
                                            ...newBindings[existingIndex],
                                            ...patch
                                          };
                                        } else {
                                          newBindings.push(patch);
                                        }
                                        setConfigForm({ ...configForm, filter_bindings: newBindings });
                                      }}
                                    >
                                      {operatorOpts.map((op) => (
                                        <option key={op.value} value={op.value}>
                                          {op.label}
                                        </option>
                                      ))}
                                    </Form.Select>
                                  </Col>
                                  <Col md={4} className="mb-2 mb-md-0">
                                    <Form.Label className="small text-muted mb-1">Current record field</Form.Label>
                                    <Form.Select
                                      value={binding.source_field || ''}
                                      disabled={!filterBindingRequiresSourceField(opValue)}
                                      onChange={(e) => {
                                        const newBindings = [...(configForm.filter_bindings || [])];
                                        const existingIndex = newBindings.findIndex(
                                          b => b.filterable_field === filterField.field_name
                                        );

                                        if (existingIndex >= 0) {
                                          newBindings[existingIndex] = {
                                            ...newBindings[existingIndex],
                                            filterable_field: filterField.field_name,
                                            source_field: e.target.value,
                                            operator: opValue
                                          };
                                        } else {
                                          newBindings.push({
                                            filterable_field: filterField.field_name,
                                            source_field: e.target.value,
                                            operator: opValue
                                          });
                                        }

                                        setConfigForm({ ...configForm, filter_bindings: newBindings });
                                      }}
                                    >
                                      <option value="">
                                        {filterBindingRequiresSourceField(opValue)
                                          ? 'Select field from current record...'
                                          : 'Not required'}
                                      </option>
                                      {sourceObjectFields.map(f => (
                                        <option key={f.field_name} value={f.field_name}>
                                          {f.field_label} ({`{{${objectName}.${f.field_name}}}`})
                                        </option>
                                      ))}
                                    </Form.Select>
                                  </Col>
                                  <Col md={1} className="text-md-center mb-2 mb-md-0">
                                    {isPersistableFilterBinding({
                                      filterable_field: filterField.field_name,
                                      source_field: binding.source_field,
                                      operator: opValue
                                    }) ? (
                                      <Badge bg="success">Bound</Badge>
                                    ) : null}
                                  </Col>
                                </Row>
                              );
                            })
                          ) : (
                            <Alert variant="info" className="mb-0">
                              <FaInfoCircle className="me-2" />
                              No filterable fields configured for this search view. All records will be shown.
                            </Alert>
                          )}
                        </Form.Group>

                        <Form.Group className="mb-3">
                          <Form.Label>Max Records</Form.Label>
                          <Form.Control
                            type="number"
                            value={configForm.maxRecords}
                            onChange={(e) => setConfigForm({
                              ...configForm,
                              maxRecords: parseInt(e.target.value) || 10
                            })}
                            min="1"
                            max="50"
                          />
                          <Form.Text className="text-muted">
                            Maximum number of records to display in the widget
                          </Form.Text>
                        </Form.Group>

                        <Form.Group className="mb-3">
                          <Form.Label>Max Height (px)</Form.Label>
                          <Form.Control
                            type="number"
                            min="200"
                            max="1000"
                            step="50"
                            value={configForm.maxHeight}
                            onChange={(e) => setConfigForm({
                              ...configForm,
                              maxHeight: parseInt(e.target.value) || 400
                            })}
                            placeholder="Maximum height in pixels"
                          />
                          <Form.Text className="text-muted">
                            Maximum height of the widget card in pixels (200-1000). Content will scroll if it exceeds this height.
                          </Form.Text>
                        </Form.Group>

                        <Form.Group className="mb-3">
                          <Form.Check
                            type="checkbox"
                            id="search-widget-display-table"
                            label="Show results as a table (View / Edit / Clone / Delete actions match list view)"
                            checked={configForm.display_as_table === true}
                            onChange={(e) =>
                              setConfigForm({
                                ...configForm,
                                display_as_table: e.target.checked
                              })
                            }
                          />
                        </Form.Group>

                        <Alert variant="info">
                          <FaInfoCircle className="me-2" />
                          The Search widget displays search results based on the configured search view. Field bindings automatically filter results using values from the current record.
                        </Alert>
                      </>
                    )}
                  </>
                )}
              </Form>
              );
            })()}
          </Modal.Body>
          <Modal.Footer>
            <Button
              type="button"
              variant="secondary"
              onClick={() => {
                setShowConfigModal(false);
                setEditingWidgetId(null);
                setWidgetDraftKind(null);
              }}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="primary"
              onClick={editingWidgetId ? handleUpdateWidget : handleSaveWidget}
              disabled={loading ||
                (!selectedRelatedList && configForm.widget_label.trim() === '') ||
                (selectedRelatedList &&
                 !(selectedRelatedList.target_object_name === 'activity' &&
                   selectedRelatedList.source_field_name === 'id' &&
                   selectedRelatedList.target_field_name === 'related_record_id') &&
                 configForm.fields.length === 0) ||
                (widgetDraftKind === 'search' && !configForm.search_view_id) ||
                (widgetDraftKind === 'map' && (() => {
                  // For each mode the dropdown selects, check the right number of fields are picked.
                  const checkCount = (modeKey, fields) => {
                    const m = modeKey || 'combined';
                    const n = (fields || []).length;
                    if (m === 'combined') return n !== 1;
                    if (m === 'split') return n !== 2;
                    return n === 0; // address: at least 1
                  };
                  if (configForm.map_data_source === 'route') {
                    if (!configForm.map_route_related_list_id) return true;
                    return checkCount(configForm.map_route_coord_source_mode, configForm.map_route_address_fields);
                  }
                  return checkCount(configForm.map_coord_source_mode, configForm.map_address_fields);
                })())
              }
            >
              {loading ? 'Saving...' : (editingWidgetId ? 'Update Widget' : 'Add Widget')}
            </Button>
          </Modal.Footer>
        </Modal>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default LayoutWidgetManager;
